import { z } from 'zod'
import { GuardReasonSchema } from '@/lib/api/schemas/stock-guard.schema'
import { YmEconomicsPieceSchema } from '@/lib/api/schemas/ymarket-pricing.schema'

/**
 * A published member, or a value from a server this browser has not been
 * rebuilt for. Every reason on this surface is read this way on purpose: the
 * closed set is what the labels bind to, while the wire stays lenient, because
 * refusing an unknown reason would blank the page the reason exists to explain.
 * A state that selects a rendering is NOT read this way - there the strict
 * enum below is the contract, and a refusal is the safer failure.
 */
export type DeploySkewed<Member extends string> = Member | (string & {})

export const InventoryDiscrepancyReasonSchema = z.enum([
  'missing_binding',
  'conflicting_binding',
  'price_divergence',
  'stock_divergence',
  'stale_observation',
  'recent_movement',
  'synchronization_failure',
])
export const InventoryIncludeFacetSchema = z.enum([
  'storefrontPrices',
  'presence',
  'readiness',
  'ymarketEconomics',
])
export const BindingScopeSchema = z.enum(['product', 'variant'])
export const BindingStateSchema = z.enum(['mapped', 'missing', 'conflicting', 'inactive'])
export const SourceKindSchema = z.enum(['onec', 'marketplace'])
export const ListingStateSourceSchema = z.enum(['product_publication', 'ymarket_catalog'])
export const WarehouseSourceSchema = z.enum(['Яндекс Маркет', 'Ozon', '1С', 'Wildberries'])
export const WarehouseProductTypeCodeSchema = z.enum([
  'ring',
  'earrings',
  'pendant',
  'chain',
  'bracelet',
  'necklace',
  'brooch',
  'piercing',
  'wedding_ring',
  'cufflinks',
])
export const DescriptionPresenceSchema = z.enum(['present', 'absent'])
// The evidence family that decided the read-time activity status. The stored
// piece lifecycle is the separate three-member UnitLifecycle vocabulary below.
export const ProductActivityStatusSchema = z.enum([
  'processing', 'delivery', 'delivered', 'cancelled', 'returning', 'returned',
  'delivery_issue', 'in_stock', 'reserved', 'out_of_stock', 'sold', 'withdrawn',
  'unknown',
])
// The ONE answer: the sentence the status column shows and the member the
// status filter selects over. The browser translates nothing into it.
export const PublishedProductStatusSchema = z.enum([
  'in_stock', 'reserved', 'sold', 'withdrawn', 'out_of_stock', 'in_transit',
  'being_returned', 'delivery_issue', 'conflict', 'unknown',
])
export const WarehouseStatusScopeSchema = z.enum([
  'all', ...PublishedProductStatusSchema.options,
])
export const ProductActivityDirectionSchema = z.enum(['none', 'outbound', 'return'])
// `conflict` is not a fourth source: it is the server saying two distinct
// observations carry the same instant and neither was ranked above the other.
export const ProductMovementSourceSchema = z.enum([
  'order', 'cdek_event', 'onec_capture', 'none',
  'carrier_event', 'marketplace_presence', 'conflict',
])
export const ProductStatusUnavailableReasonSchema = z.enum([
  'status.no_order_no_stock',
  'status.order_not_found',
  'status.order_status_unknown',
  'status.order_movement_unavailable',
  'status.multiple_order_attachments',
  'status.order_attachment_invalid',
  'status.movement_source_conflict',
])
export const FreshnessStateSchema = z.enum(['fresh', 'stale', 'unavailable', 'failed'])
// Three populations, asked for one at a time and never added together. A row
// is a `piece`; `unresolved_subject` is a subject nobody has matched, which a
// person can act on today; `merge_survived` is a subject a merge folded away,
// which the decision endpoint refuses until the owner releases it.
export const InventoryRowGrainSchema = z.enum(['piece', 'unresolved_subject', 'merge_survived'])
export const InventoryReviewStateSchema = z.enum(['ready', 'needs_review', 'conflict', 'unavailable'])
export const InventoryStockStateSchema = z.enum(['confirmed', 'explicit_zero', 'unknown', 'stale', 'unavailable'])
// One lifecycle vocabulary, mirrored once. The identified-unit row, the per-unit
// listing and the state 1C recorded on a variant all read it, and two spellings
// of one closed set is how one piece comes to read two ways on two screens.
export const UnitLifecycleSchema = z.enum(['in_stock', 'in_transit', 'sold'])
// Product-owned lifecycle. A missing value is kept as a typed absence rather
// than inferred from quantity or an ingest timestamp.
export const InventoryProductStateSchema = z.enum([
  'in_stock',
  'in_transit',
  'sold',
])
export const InventoryMovementKindSchema = z.enum([
  'marketplace_order',
  'onec_offline_sale',
  'onec_inventory_set',
  'onec_reservation',
])
export const InventoryReconciliationStateSchema = z.enum([
  'confirmed',
  'awaiting_1c',
  'stale',
  'unavailable',
  'requires_review',
])
export const AvailabilityHoldStateSchema = z.enum(['awaiting_1c', 'confirmed', 'requires_review'])

// The published Warehouse reason vocabularies, mirrored from
// `forge/inventory/reason_vocabulary.py`. Each family carries its own
// `*_reason_not_published` member: the server maps a retired member, a raw
// import token or a value it cannot publish onto it, so the browser is never
// handed an engineering token to render.
export const WarehouseFactReasonSchema = z.enum([
  'product_type.not_recorded',
  'shape.not_recorded',
  'material.not_recorded',
  'material.unsupported_source_value',
  'model.material_not_evidenced',
  'fineness.not_recorded',
  'model.fineness_not_evidenced',
  'unit.weight_not_recorded',
  'unit.marking_not_recorded_in_source',
  'state.not_recorded',
  'state.onec_registers_silent',
  'state.onec_sold_again_without_return',
  'state.onec_reserved_not_on_hand',
  'arrival.not_recorded',
  'weight.authoritative_net_missing',
  'weight.package_only_forbidden',
  'metal_weight.not_extracted',
  'metal_weight.not_recorded',
  'metal_weight.invalid_in_onec',
  'insert_weight.not_extracted',
  'insert_weight.none_recorded',
  'insert_weight.invalid_in_onec',
  'insert_carats.not_extracted',
  'insert_carats.not_stated',
  'insert_carats.spec_truncated',
  'insert_carats.invalid_in_onec',
  'insert_stones.not_extracted',
  'insert_stones.none_recorded',
  'insert_stones.total_only',
  'corrected_metal.composition_not_extracted',
  'corrected_metal.stone_spec_truncated',
  'corrected_metal.stone_kind_not_recorded',
  'corrected_metal.stone_kind_ambiguous',
  'corrected_metal.precious_weight_not_recorded',
  'corrected_metal.precious_weight_invalid',
  'fact.reason_not_published',
])
// The ungrouped Warehouse table and model-group feed retain rows whose identity
// facts are absent instead of silently dropping stock from the response.
export const WarehouseIdentityReasonSchema = z.enum([
  'manufacturer.not_recorded',
  'brand.not_recorded',
  'brand.not_captured',
  'brand.conflicting_sources',
  'article.not_recorded',
  'tech_size.not_recorded',
  'title.not_recorded',
  'description.not_recorded',
  'unit.marking_not_recorded_in_source',
  // A subject we hold that no import resolved to a source identity, and one a
  // merge folded away. Two different sentences on purpose: the first is open
  // for a person to settle, the second is held until the owner releases it.
  'identity.source_identity_not_resolved',
  'identity.previously_merged',
  'identity.reason_not_published',
])
export const ModelGroupingReasonSchema = z.enum([
  'model.grouping_not_reviewed',
  'model.reason_not_published',
])
export const MarketplaceListingReasonSchema = z.enum([
  'price.not_provided',
  'stock.provider_observation_absent',
  'listing.observation_absent',
  'provider.account_not_configured',
  'listing.reason_not_published',
])
export const SourceProjectionReasonSchema = z.enum([
  'inventory.observation_absent',
  'inventory.observation_stale',
  'inventory.sync_failed',
  'inventory.snapshot_incomplete',
  'inventory.price_unavailable',
  'price.not_provided',
  'price.not_recorded',
  'inventory.stock_unavailable',
  'stock.publication_evidence_absent',
  'stock.product_movement_unavailable',
  'stock.product_movement_conflict',
  'inventory.reason_not_published',
])
export const ComparableFieldReasonSchema = z.enum([
  'title.not_recorded',
  'seller_sku.not_recorded',
  'price.not_provided',
  'currency.not_recorded',
  'stock.provider_observation_absent',
  'listing_status.not_recorded',
  'material.not_recorded',
  'fineness.not_recorded',
  'product_type.not_recorded',
  'shape.not_recorded',
  'tech_size.not_recorded',
  'unit.weight_not_recorded',
  'field.reason_not_published',
])
export const FrozenMappingRefusalReasonSchema = z.enum([
  'exact_source_identity',
  'reviewed_crosswalk',
  'unknown_variant',
  'duplicate_seller_sku',
  'duplicate_order_item',
  'title_only_forbidden',
  'name_only_forbidden',
  'missing_marketplace_mapping',
  'frozen_mapping.reason_not_published',
])

const BindingSchema = z.object({ kind: z.string(), value: z.string(), scope: BindingScopeSchema }).strict()

const WarehousePositionSchema = z.object({
  warehouseCode: z.string(),
  warehouseName: z.string().nullable(),
  onHand: z.number().int().nonnegative().nullable(),
  available: z.number().int().nonnegative().nullable(),
  reserved: z.number().int().nonnegative().nullable(),
  inFlight: z.number().int().nonnegative().nullable(),
  // SourceProjectionReasonSchema, read leniently. See DeploySkewed above.
  unavailableReason: z.string().nullable(),
  observedAt: z.iso.datetime({ offset: true }),
}).strict()

// A Python `Decimal` reaches the wire as a string; both spellings are accepted
// and normalized to a number so a caller never has to know which arrived.
const decimalOnTheWire = z.union([z.number(), z.string().regex(/^\d+(?:\.\d+)?$/).transform(Number)]).nullable()
const money = decimalOnTheWire
const timestamp = z.iso.datetime({ offset: true })

export const InventorySourceSchema = z.object({
  accountId: z.string().uuid(),
  accountCode: z.string(),
  providerCode: z.string(),
  displayName: z.string(),
  sourceKind: SourceKindSchema,
  isActive: z.boolean(),
  bindingState: BindingStateSchema,
  bindings: z.array(BindingSchema),
  listingState: z.string().nullable(),
  listingStateSource: ListingStateSourceSchema.nullable().optional(),
  price: money,
  currency: z.string().nullable(),
  // SourceProjectionReasonSchema on all three, read leniently.
  priceUnavailableReason: z.string().nullable(),
  priceObservedAt: timestamp.nullable(),
  oldPrice: money.nullable().optional(),
  oldPriceUnavailableReason: z.string().nullable().optional(),
  warehouses: z.array(WarehousePositionSchema),
  lastSuccessfulSyncAt: timestamp.nullable(),
  freshness: FreshnessStateSchema,
  freshnessReason: z.string().nullable(),
  synchronizationFailure: z.string().nullable(),
  protectiveHold: z.number().int().nonnegative(),
  effectiveAvailable: z.number().int().nonnegative().nullable(),
}).strict()

const MovementSchema = z.object({
  kind: InventoryMovementKindSchema,
  sourceAccountCode: z.string(),
  quantity: z.number().int().positive(),
  occurredAt: timestamp,
  reconciliationState: InventoryReconciliationStateSchema,
}).strict()

const HoldSchema = z.object({
  targetAccountCode: z.string(),
  sourceAccountCode: z.string(),
  quantity: z.number().int().positive(),
  createdAt: timestamp,
  state: AvailabilityHoldStateSchema,
}).strict()

const DiscrepancySchema = z.object({
  // A reason, so it is read leniently: a member added on the server must reach
  // the Russian fallback rather than refuse the variant that carries it.
  reason: z.string(),
  sourceAccountCodes: z.array(z.string()),
  observedAt: timestamp.nullable(),
  evidenceCount: z.number().int().nonnegative(),
}).strict()

const FrozenRegistrySchema = z.object({
  inputDigest: z.string().regex(/^[a-f0-9]{64}$/),
  sourceAdapterId: z.string(),
  productType: z.string(),
  shape: z.string().nullable(),
  // WarehouseFactReasonSchema on all four, read leniently.
  shapeUnavailableReason: z.string().nullable(),
  material: z.string().nullable(),
  materialUnavailableReason: z.string().nullable(),
  fineness: z.string().nullable(),
  finenessUnavailableReason: z.string().nullable(),
  netWeightGrams: money,
  netWeightUnavailableReason: z.string().nullable(),
  physicalUnitCount: z.number().int().nonnegative(),
  acceptedMappingCount: z.number().int().nonnegative(),
  refusedMappingCount: z.number().int().nonnegative(),
  // FrozenMappingRefusalReasonSchema, not MatchReason: the frozen registry
  // refuses a mapping with its own review vocabulary.
  refusedMappingReasons: z.array(z.string()),
}).strict()

// almaz.product-graph/v2 additive members, owned by task_00julr. Each is
// optional so a server that has not shipped it yet still parses under strict
// object mode. The closed vocabularies below are frozen with that lane and are
// never widened locally to accommodate an unexpected value.

export const MarketplacePresenceStateSchema = z.enum(['present', 'absent', 'unknown'])
export const ProviderConnectionStateSchema = z.enum(['connected', 'not_connected', 'no_observation'])
export const FieldDiffStateSchema = z.enum([
  'equal',
  'different',
  'missingCanonical',
  'missingSource',
  'stale',
  'unsupported',
  'incompatible',
  'conflict',
])
export const FieldAuthoritySchema = z.enum([
  'canonical',
  'onec_frozen_registry',
  'provider_account',
  'operator_review',
])
export const ComparableFieldSchema = z.enum([
  'title',
  'sellerSku',
  'price',
  'currency',
  'stock',
  'listingStatus',
  'material',
  'fineness',
  'productType',
  'shape',
  'techSize',
  'netWeightGrams',
])
export const MatchRuleSchema = z.enum([
  'exact_physical_unit_uin',
  'reviewed_alias',
  'unique_article_size',
  'unique_article_size_without_manufacturer',
])
export const MatchBucketSchema = z.enum(['matched', 'unmatched', 'conflict', 'manual_review', 'unavailable'])
// The read-side fallback when a persisted match names a member this build
// does not know (deploy skew). It is never a write vocabulary.
export const MatchReadUnavailableReasonSchema = z.enum(['match.unknown_persisted_vocabulary'])
export const MatchReasonSchema = z.enum([
  'exact_physical_unit_uin',
  'reviewed_alias',
  'unique_article_size',
  'unique_article_size_without_manufacturer',
  'canonical_source_of_record',
  'no_source_identity_evidence',
  'offer_id_missing',
  'no_reviewed_mapping',
  'refused_marketplace_mapping',
  'malformed_onec_code',
  'duplicate_tuple',
  'manufacturer_disagreement',
  'duplicate_alias_value',
  'source_local_collision',
  'canonical_product_mismatch',
  'contradictory_material',
  'contradictory_fineness',
  'contradictory_shape',
  'contradictory_size',
  'contradictory_net_weight',
  'ambiguous_size',
  'title_only_match',
  'fuzzy_sku_match',
  'ambiguous_variant_match',
  'unknown_source_variant',
  'no_provider_qualified_alias',
  'source_snapshot_absent',
  'package_weight_only',
  'material_evidence_absent',
  'fineness_evidence_absent',
  'net_weight_evidence_absent',
])
export const ProposalDecisionSchema = z.enum([
  'acceptSourceIntoCanonical',
  'keepCanonical',
  'compileCanonicalToTargets',
  'ignoreForTarget',
  'manualReview',
])
export const ProposalStateSchema = z.enum(['draft', 'reviewed', 'applied', 'rejected'])
export const OnecCodeParseStateSchema = z.enum(['parsed', 'malformed', 'unavailable'])
export const IdentityOriginSchema = z.enum(['existing_resolution', 'new_candidate'])
export const LocalStockStateSchema = z.enum(['in_stock', 'out_of_stock', 'unknown'])
export const MissingTaxonomyFacetSchema = z.enum(['productType', 'shape', 'material', 'fineness', 'any'])
export const TaxonomyFactSchema = MissingTaxonomyFacetSchema.exclude(['any'])
// «Связь с площадкой»: one member per piece; `missing` is the «Не связано с площадкой» chip.
export const MarketplaceBindingSchema = z.enum(['bound', 'missing', 'conflicting'])

export const ProvenanceSchema = z.object({
  contractVersion: z.string(),
  sourceAdapterId: z.string().nullable(),
  parserVersion: z.string(),
  matchRuleVersion: z.string(),
  evidenceDigest: z.string(),
  observedAt: timestamp.nullable(),
  recordedAt: timestamp.nullable(),
}).strict()

// canonicalProductId is persisted once at accepted creation and is never
// re-derived from article, size or manufacturer; proposalCandidateId is the
// proposal-time id and must never be keyed on. It names the piece: a client key
// still called `variant` while carrying a product id is a lie the next reader
// would believe.
export const VariantIdentitySchema = z.object({
  canonicalProductId: z.string().uuid(),
  proposalCandidateId: z.string().uuid().nullable(),
  identityOrigin: IdentityOriginSchema.nullable(),
  identityNamespaceVersion: z.string(),
  article: z.string().nullable(),
  normalizedSize: z.string().nullable(),
  rawSourceKey: z.string().nullable(),
  onecCodeParseState: OnecCodeParseStateSchema.nullable(),
  manufacturer: z.string().nullable(),
  // WarehouseIdentityReasonSchema on both, read leniently.
  manufacturerUnavailableReason: z.string().nullable(),
  articleUnavailableReason: z.string().nullable(),
  brand: z.string().nullable(),
  brandUnavailableReason: z.string().nullable(),
}).strict().refine(
  (identity) => (identity.article === null) !== (identity.articleUnavailableReason === null),
  { message: 'article and its unavailable reason are exclusive' },
)

// Every stored type travels: the nine storefront codes, 1C's `unspecified`
// and any other code a writer recorded. Labels say which is which.
export const StoredProductTypeSchema = z.string().min(1).max(60)

export const ProductAttributesSchema = z.object({
  productType: StoredProductTypeSchema.nullable(),
  // WarehouseFactReasonSchema on all five, read leniently.
  productTypeUnavailableReason: z.string().nullable(),
  shape: z.string().nullable(),
  shapeUnavailableReason: z.string().nullable(),
  material: z.string().nullable(),
  materialUnavailableReason: z.string().nullable(),
  fineness: z.string().nullable(),
  finenessUnavailableReason: z.string().nullable(),
  netWeightGrams: money,
  netWeightUnavailableReason: z.string().nullable(),
}).strict()

export const MarketplacePresenceSchema = z.object({
  accountCode: z.string(),
  presence: MarketplacePresenceStateSchema,
  observedAt: timestamp.nullable(),
}).strict()

// One row per (providerCode, accountCode, externalOfferId). A connected
// provider with no observation for this variant stays distinguishable from a
// provider that is not connected at all.
export const MarketplaceListingSchema = z.object({
  providerCode: z.string(),
  accountCode: z.string().nullable(),
  displayName: z.string(),
  externalOfferId: z.string().nullable(),
  sellerSku: z.string().nullable(),
  title: z.string().nullable(),
  price: money,
  // MarketplaceListingReasonSchema on all three, read leniently.
  priceUnavailableReason: z.string().nullable(),
  currency: z.string().nullable(),
  stock: z.number().int().nonnegative().nullable(),
  stockUnavailableReason: z.string().nullable(),
  listingStatus: z.string().nullable(),
  observedAt: timestamp.nullable(),
  evidenceDigest: z.string().nullable(),
  connectionState: ProviderConnectionStateSchema,
  unavailableReason: z.string().nullable(),
  oldPrice: money.nullish(),
  oldPriceUnavailableReason: z.string().nullable().optional(),
  priceObservedAt: timestamp.nullable().optional(),
  provenance: ProvenanceSchema.nullish(),
}).strict()

export const FieldObservationSchema = z.object({
  accountCode: z.string().nullable(),
  value: z.string().nullable(),
  // ComparableFieldReasonSchema, read leniently.
  unavailableReason: z.string().nullable(),
  state: FieldDiffStateSchema,
  observedAt: timestamp.nullable(),
  note: z.string().nullable(),
  provenance: ProvenanceSchema,
}).strict()

export const FieldComparisonSchema = z.object({
  field: ComparableFieldSchema,
  canonical: z.object({
    value: z.string().nullable(),
    // ComparableFieldReasonSchema, read leniently.
    unavailableReason: z.string().nullable(),
    authority: FieldAuthoritySchema,
  }).strict(),
  observations: z.array(FieldObservationSchema),
}).strict()

// A row exists only when both quantities are present and differ: an
// unavailable quantity never compares as zero and never produces a row alone.
export const StockDisagreementSchema = z.object({
  accountCode: z.string(),
  canonicalQuantity: z.number().int().nonnegative().nullable(),
  // SourceProjectionReasonSchema on both, read leniently.
  canonicalUnavailableReason: z.string().nullable(),
  observedQuantity: z.number().int().nonnegative().nullable(),
  observedUnavailableReason: z.string().nullable(),
  observedAt: timestamp.nullable(),
  reason: z.string(),
}).strict()

export const StockMismatchReasonSchema = z.enum([
  'stock_divergence',
  'marketplace_reported_zero',
  'marketplace_never_reported',
  'marketplace_quantity_unavailable',
  'onec_observation_absent',
])

export const StockMismatchFindingSchema = z.object({
  ourQuantity: z.number().int().nonnegative().nullable(),
  ourAuthority: z.string().nullable(),
  ourAccountCode: z.string().nullable(),
  ourAsOf: timestamp.nullable(),
  marketplaceQuantity: z.number().int().nonnegative().nullable(),
  marketplaceAccountCode: z.string(),
  marketplaceObservedAt: timestamp.nullable(),
  reason: StockMismatchReasonSchema,
}).strict()

export const StockMismatchPieceSchema = z.object({
  productId: z.string().uuid(),
  article: z.string().nullable(),
  title: z.string().nullable(),
  techSize: z.string().nullable(),
  uin: z.string().nullable(),
  findings: z.array(StockMismatchFindingSchema),
}).strict()

export const StockMismatchSignalSchema = z.object({
  count: z.number().int().nonnegative(),
  items: z.array(StockMismatchPieceSchema),
}).strict()

// Review-only in this phase. Proposal identity is deliberately distinct from
// the durable canonical identity and never substitutes for it.
export const ReconciliationProposalSchema = z.object({
  proposalId: z.string().uuid(),
  proposalVersion: z.number().int().nonnegative(),
  field: ComparableFieldSchema,
  targetAccountCode: z.string().nullable(),
  decision: ProposalDecisionSchema,
  sourceHash: z.string(),
  canonicalHash: z.string(),
  targetHash: z.string().nullable(),
  state: ProposalStateSchema,
  createdAt: timestamp,
}).strict()

export const ProductOrderReferenceSchema = z.object({
  sourceId: z.string(),
  externalOrderId: z.string(),
  position: z.number().int(),
  matchRule: z.string(),
  matchReason: z.string(),
  evidenceDigest: z.string(),
}).strict()

export const ProductStatusSchema = z.object({
  // The row already names the Product; the server repeats it here and the
  // reader does not use it.
  productId: z.string().uuid().optional(),
  status: ProductActivityStatusSchema,
  publishedStatus: PublishedProductStatusSchema,
  direction: ProductActivityDirectionSchema,
  movementAt: z.string().nullable(),
  movementSource: ProductMovementSourceSchema,
  unavailableReason: z.string().nullable().optional().default(null),
  orderReference: ProductOrderReferenceSchema.nullish(),
}).strict()

export const StorefrontPriceSchema = z.object({
  target: z.enum(['ymarket', 'wildberries', 'ozon', 'ori_site']),
  connectionState: ProviderConnectionStateSchema,
  listed: z.boolean(),
  price: money.nullable(),
  oldPrice: money.nullable(),
  currency: z.string().nullable(),
  listingState: z.string().nullable(),
  listingStateSource: ListingStateSourceSchema.nullable().optional(),
  placementState: MarketplacePresenceStateSchema.optional(),
  observedAt: timestamp.nullable(),
  unavailableReason: z.string().nullable(),
  oldPriceUnavailableReason: z.string().nullable().optional(),
  priceObservedAt: timestamp.nullable().optional(),
  minimumForBestseller: money.nullable().optional(),
  estimatedBuyerPrice: money.nullable().optional(),
  subsidyPercent: money.nullable().optional(),
}).strict()

export const ReadinessIssueSchema = z.object({
  code: z.string(),
  severity: z.enum(['blocking', 'advisory']),
  label: z.string().min(1),
  detail: z.string().nullable().optional(),
}).strict()

export const StorefrontReadinessSchema = z.object({
  target: z.enum(['ymarket', 'wildberries', 'ozon', 'ori_site']),
  ready: z.boolean(),
  issues: z.array(ReadinessIssueSchema),
}).strict()

export const InventoryMediaIssueCodeSchema = z.enum([
  'media.download_failed',
  'media.download_pending',
  'media.probe_unavailable',
  'wildberries.photo_dimensions',
  'wildberries.photo_format',
  'wildberries.photo_size',
  'wildberries.photo_limit',
  'wildberries.video_limit',
  'wildberries.video_unavailable',
  'wildberries.publication_blocked',
])

export const InventoryProblemSchema = z.object({
  code: InventoryMediaIssueCodeSchema,
  label: z.string().min(1),
  scope: z.enum(['generic', 'wildberries']),
  sourceEvidence: z.string().nullable(),
  actionable: z.boolean(),
  unavailable: z.boolean(),
  detail: z.string().nullable().optional().default(null),
  kind: z.string().nullable().optional().default(null),
  position: z.number().int().positive().nullable().optional().default(null),
}).strict()

// One entry of 1C's stone spec as written: `kind` keeps 1C's abbreviation
// («Бр 57 Кр») and `unit` is `ct` only where 1C wrote it.
export const InsertStoneSchema = z.object({
  kind: z.string(),
  count: z.number().int().nonnegative().nullable(),
  weight: z.string().nullable(),
  unit: z.literal('ct').nullable(),
  grade: z.string().nullable(),
}).strict()

export const InsertStonesSchema = z.object({
  source: z.string(),
  complete: z.boolean(),
  stones: z.array(InsertStoneSchema),
}).strict()

export const CompositionStoneKindSchema = z.enum([
  'diamond',
  'sapphire',
  'emerald',
  'ruby',
  'brillianite',
  'cubic_zirconia',
  'fianite',
  'synthetic',
  'swarovski',
  'crystal',
  'enamel',
  'other',
])
export const CompositionStoneOriginSchema = z.enum(['natural', 'simulant'])
export const PieceStoneSummaryItemSchema = z.object({
  kind: CompositionStoneKindSchema,
  carats: money,
  origin: CompositionStoneOriginSchema,
  count: z.number().int().nonnegative().nullable(),
}).strict()

// Raw 1C composition is kept beside the convention-derived calculator inputs.
export const PieceCompositionSchema = z.object({
  rawSource: z.literal('1c.yutd-10.7'),
  rawConventionVersion: z.literal('yutd-10.7-natural-precious-unmarked-weight-is-carat-v1@2026-10-01'),
  metalWeightGrams: money,
  metalWeightUnavailableReason: WarehouseFactReasonSchema.nullable(),
  insertWeightGrams: money,
  insertWeightUnavailableReason: WarehouseFactReasonSchema.nullable(),
  insertWeightCarats: money,
  insertWeightCaratsUnavailableReason: WarehouseFactReasonSchema.nullable(),
  insertStones: InsertStonesSchema.nullable(),
  insertStonesUnavailableReason: WarehouseFactReasonSchema.nullable(),
  reportedWeightGrams: money,
  derivedSource: z.literal('forge.onec_sync.piece_composition'),
  derivedConventionVersion: z.literal('forge-corrected-metal-reported-plus-0.8-precious-ct-v1@2026-10-01'),
  correctedMetalWeightGrams: money,
  correctedMetalWeightUnavailableReason: WarehouseFactReasonSchema.nullable(),
  preciousStoneSummary: z.array(PieceStoneSummaryItemSchema),
  preciousStoneSummaryUnavailableReason: WarehouseFactReasonSchema.nullable(),
}).strict().superRefine((composition, context) => {
  const pairs = [
    ['metalWeightGrams', composition.metalWeightGrams, composition.metalWeightUnavailableReason],
    ['insertWeightGrams', composition.insertWeightGrams, composition.insertWeightUnavailableReason],
    ['insertWeightCarats', composition.insertWeightCarats, composition.insertWeightCaratsUnavailableReason],
    ['insertStones', composition.insertStones, composition.insertStonesUnavailableReason],
    ['correctedMetalWeightGrams', composition.correctedMetalWeightGrams, composition.correctedMetalWeightUnavailableReason],
  ] as const
  for (const [path, value, reason] of pairs) {
    if ((value === null) === (reason === null)) {
      context.addIssue({ code: 'custom', path: [path], message: `${path} carries a value or a typed reason` })
    }
  }
})
export type PieceComposition = z.infer<typeof PieceCompositionSchema>
export type InsertStones = z.infer<typeof InsertStonesSchema>
export type PieceStoneSummaryItem = z.infer<typeof PieceStoneSummaryItemSchema>

// A list row carries only what the Warehouse list and its actions read; the
// piece card below carries every member in full.
export const ListSourceSchema = InventorySourceSchema.pick({
  accountCode: true,
  providerCode: true,
  sourceKind: true,
  isActive: true,
  listingState: true,
  listingStateSource: true,
  freshness: true,
  freshnessReason: true,
  effectiveAvailable: true,
}).extend({
  warehouses: z.array(WarehousePositionSchema.pick({ observedAt: true })),
})
const ListDiscrepancySchema = DiscrepancySchema.pick({ reason: true })
export const ListMarketplaceListingSchema = MarketplaceListingSchema.pick({
  providerCode: true,
  accountCode: true,
  displayName: true,
  listingStatus: true,
  observedAt: true,
  connectionState: true,
  unavailableReason: true,
})
export const ListStockMismatchFindingSchema = StockMismatchFindingSchema.pick({
  marketplaceAccountCode: true,
  reason: true,
})

const InventoryListItemShape = z.object({
  // Null when no reviewed ProductModel groups this product. The reason is a
  // real state, not missing data, and never a licence to synthesise a model.
  modelId: z.string().uuid().nullable(),
  modelArticle: z.string().nullable(),
  // ModelGroupingReasonSchema, read leniently.
  modelUnavailableReason: z.string().nullable(),
  productId: z.string().uuid(),
  productStatus: ProductStatusSchema.nullish(),
  title: z.string().nullable(),
  titleUnavailableReason: z.string().nullable().optional(),
  brand: z.string().nullable(),
  brandUnavailableReason: z.string().nullable().optional(),
  article: z.string().nullable().optional(),
  uin: z.string().nullable().optional(),
  uinUnavailableReason: z.string().nullable().optional(),
  techSize: z.string().nullable(),
  reconciliationState: InventoryReconciliationStateSchema,
  sources: z.array(ListSourceSchema),
  holds: z.array(HoldSchema),
  movements: z.array(MovementSchema),
  discrepancies: z.array(ListDiscrepancySchema),
  frozenRegistry: FrozenRegistrySchema.nullable(),
  attributes: ProductAttributesSchema,
  marketplacePresence: z.array(MarketplacePresenceSchema).nullish(),
  listings: z.array(ListMarketplaceListingSchema).nullish(),
  stockDisagreements: z.array(StockDisagreementSchema).nullish(),
  stockMismatchFindings: z.array(ListStockMismatchFindingSchema).optional().default([]),
  listingTotal: z.number().int().nonnegative(),
  holdTotal: z.number().int().nonnegative(),
  movementTotal: z.number().int().nonnegative(),
  salesTotal: z.number().int().nonnegative(),
  stockDisagreementTotal: z.number().int().nonnegative(),
  photoCount: z.number().int().nonnegative(),
  // This product's own chosen tile, decided by the shared server media rule. A
  // reference travels only for a downloaded row whose evidence object resolved;
  // every other state names itself, so an empty square is a stated absence.
  primaryMediaUrl: z.string().nullable().optional().default(null),
  // ProductMediaUnavailableReasonSchema, read leniently and declared here
  // because that enum is defined further down this module. A member added on
  // the server must degrade to an empty tile, never reject the whole row.
  primaryMediaUnavailableReason: z.string().nullable().optional().default(null),
  // MatchRuleSchema and MatchReasonSchema, read leniently: both explain a
  // bucket, and a member added on the server must degrade to Russian rather
  // than refuse the row. The bucket itself drives a filter and stays closed.
  matchRule: z.string().nullish(),
  matchReason: z.string().nullish(),
  matchUnavailableReason: z.string().nullish(),
  matchBucket: MatchBucketSchema.nullish(),
  rowGrain: InventoryRowGrainSchema.optional().default('piece'),
  reviewState: InventoryReviewStateSchema.optional().default('ready'),
  physicalPieceCount: z.number().int().nonnegative().optional().default(0),
  unresolvedPopulationCount: z.number().int().nonnegative().optional().default(0),
  // What 1C recorded about the piece, or the reason it recorded none. Exactly
  // one of the pair is set, and neither is ever read as the published status.
  currentState: UnitLifecycleSchema.nullable(),
  currentStateUnavailableReason: WarehouseFactReasonSchema.nullable(),
  // The actual 1C receipt date, not the time this Product was created here.
  // It carries the same value-or-reason contract as the other piece facts.
  onecArrivedAt: timestamp.nullable(),
  onecArrivedAtUnavailableReason: WarehouseFactReasonSchema.nullable(),
  // 1C's metal and insert facts; a frozen export row has no Product and carries none.
  composition: PieceCompositionSchema.nullable().optional().default(null),
  // Product-owned activity members are optional while older compatible readers
  // are still in service. The browser never manufactures an activity when these
  // members are absent.
  lastActivityAt: timestamp.nullable().optional().default(null),
  lastActivitySource: z.string().nullable().optional().default(null),
  lastActivityGrain: z.string().nullable().optional().default(null),
  lastActivityUnavailableReason: z.string().nullable().optional().default(null),
  onecPrice: money.nullable().optional().default(null),
  onecPriceUnavailableReason: z.string().nullable().optional().default(null),
  storefrontPrices: z.array(StorefrontPriceSchema).optional().default([]),
  storefrontReadiness: z.array(StorefrontReadinessSchema).optional().default([]),
  // Stored-snapshot YM economics; only include=ymarketEconomics fills it.
  ymarketEconomics: YmEconomicsPieceSchema.nullable().optional().default(null),
  problems: z.array(InventoryProblemSchema).optional().default([]),
  problemCount: z.number().int().nonnegative().optional().default(0),
  problemsAvailable: z.boolean().optional().default(false),
  problemsUnavailableReason: z.string().nullable().optional().default(null),
}).strict()

function checkVariantPairs(variant: z.infer<typeof InventoryListItemShape>, context: z.RefinementCtx): void {
  if ((variant.currentState === null) === (variant.currentStateUnavailableReason === null)) {
    context.addIssue({ code: 'custom', path: ['currentState'], message: 'currentState and currentStateUnavailableReason are exclusive' })
  }
  if ((variant.onecArrivedAt === null) === (variant.onecArrivedAtUnavailableReason === null)) {
    context.addIssue({ code: 'custom', path: ['onecArrivedAt'], message: 'onecArrivedAt and onecArrivedAtUnavailableReason are exclusive' })
  }
  if (variant.problemCount !== variant.problems.length) {
    context.addIssue({ code: 'custom', path: ['problemCount'], message: 'problemCount must equal problems.length' })
  }
}

/** One Warehouse list row; the published description is read from the piece card. */
export const InventoryListItemSchema = InventoryListItemShape.superRefine(checkVariantPairs)

/** The piece card: every source and listing member in full, the identity and the published description. */
export const InventoryVariantSchema = InventoryListItemShape.extend({
  sources: z.array(InventorySourceSchema),
  discrepancies: z.array(DiscrepancySchema),
  identity: VariantIdentitySchema,
  listings: z.array(MarketplaceListingSchema).nullish(),
  stockMismatchFindings: z.array(StockMismatchFindingSchema).optional().default([]),
  description: z.string().default(''),
  curatedDescription: z.string().nullable().default(null),
  descriptionSource: z.enum(['curated', 'description']).nullable().default(null),
  descriptionUnavailableReason: WarehouseIdentityReasonSchema.nullable().default(null),
}).strict().superRefine(checkVariantPairs)

export const InventoryRowSchema = z.object({
  rowKey: z.string().min(1),
  rowGrain: InventoryRowGrainSchema,
  reviewState: InventoryReviewStateSchema,
  // A row names one piece and nothing else. The two identities beside it
  // described levels this release deleted.
  productId: z.string().uuid().nullable(),
  unresolvedId: z.string().nullable(),
  title: z.string().nullable(),
  titleUnavailableReason: z.string().nullable(),
  article: z.string().nullable(),
  techSize: z.string().nullable(),
  weightGrams: money.optional().default(null),
  weightUnavailableReason: z.string().nullable().optional().default(null),
  uin: z.string().nullable(),
  uinUnavailableReason: z.string().nullable(),
  quantity: z.number().int().nonnegative().nullable(),
  quantityUnavailableReason: z.string().nullable(),
  stockState: InventoryStockStateSchema,
  stockAuthority: z.string().nullable(),
  stockObservedAt: timestamp.nullable(),
  freshness: FreshnessStateSchema.nullable(),
  sourceAccountCode: z.string().nullable(),
  unresolvedReason: z.lazy(() => z.union([
    WarehouseIdentityReasonSchema,
    SourceProjectionReasonSchema,
    UnitReaderReasonSchema,
    GuardReasonSchema,
    z.literal('product.merge_history'),
  ])).nullable(),
  conflictCount: z.number().int().nonnegative(),
  aggregateQuantity: z.number().int().nonnegative().nullable().optional().default(null),
  aggregateStockState: InventoryStockStateSchema.optional().default('unknown'),
  aggregateStockAuthority: z.string().nullable().optional().default(null),
  aggregateStockObservedAt: timestamp.nullable().optional().default(null),
  aggregateFreshness: FreshnessStateSchema.nullable().optional().default(null),
  aggregateSourceAccountCount: z.number().int().nonnegative().optional().default(0),
  primaryMediaUrl: z.string().nullable().optional().default(null),
  problems: z.array(InventoryProblemSchema).optional().default([]),
  problemCount: z.number().int().nonnegative().optional().default(0),
  problemsAvailable: z.boolean().optional().default(false),
  problemsUnavailableReason: z.string().nullable().optional().default(null),
}).strict().superRefine((row, context) => {
  if (row.problemCount !== row.problems.length) {
    context.addIssue({ code: 'custom', path: ['problemCount'], message: 'problemCount must equal problems.length' })
  }
})

// The first six count the population the rows come from, so a number in the
// header and a row in the table can never describe different sets. The last
// three count deliberately wider populations and say so in their own labels.
export const InventoryPopulationCountersSchema = z.object({
  pieceCount: z.number().int().nonnegative(),
  markedPieceCount: z.number().int().nonnegative(),
  unmarkedPieceCount: z.number().int().nonnegative(),
  photographedPieceCount: z.number().int().nonnegative(),
  stateUnavailableCount: z.number().int().nonnegative(),
  conflictCount: z.number().int().nonnegative(),
  displayedRowCount: z.number().int().nonnegative(),
  mergeSurvivedCount: z.number().int().nonnegative().optional().default(0),
  unresolvedSubjectCount: z.number().int().nonnegative().optional().default(0),
  unresolvedPopulationCount: z.number().int().nonnegative().optional().default(0),
  problemPieceCount: z.number().int().nonnegative().optional().default(0),
}).strict()

// GET /api/inventory/{productId}/reconciliation. Read-only: every diff verdict
// is the server's, and no decision here is executable.
export const VariantReconciliationSchema = z.object({
  contractVersion: z.string().nullish(),
  productId: z.string().uuid(),
  fieldComparisons: z.array(FieldComparisonSchema),
  reconciliationProposals: z.array(ReconciliationProposalSchema),
  fieldComparisonTotal: z.number().int().nonnegative(),
  reconciliationProposalTotal: z.number().int().nonnegative(),
}).strict()

export const AvailableFiltersSchema = z.object({
  materials: z.array(z.string()),
  finenesses: z.array(z.string()),
  productTypes: z.array(StoredProductTypeSchema),
  shapes: z.array(z.string()),
  techSizes: z.array(z.string()),
  manufacturers: z.array(z.string()).nullish(),
  providerCodes: z.array(WarehouseSourceSchema).nullish(),
  orderStatuses: z.array(z.string()).nullish(),
  // Pieces per value (orders per `orderStatus` value) over the same population.
  counts: z.record(z.string(), z.record(z.string(), z.number().int().nonnegative())).optional().default({}),
  // The facts at least one Warehouse piece records; absent from an older server.
  recordedTaxonomy: z.array(TaxonomyFactSchema).nullish(),
}).strict()

export const ProviderSlotSchema = z.object({
  providerCode: z.string(),
  accountCode: z.string().nullable(),
  displayName: z.string(),
  connectionState: ProviderConnectionStateSchema,
  // MarketplaceListingReasonSchema, read leniently.
  unavailableReason: z.string().nullable(),
  placementObserved: z.boolean(),
}).strict()

export const InventoryPageSchema = z.object({
  items: z.array(InventoryListItemSchema),
  total: z.number().int().nonnegative(),
  limit: z.number().int().positive(),
  offset: z.number().int().nonnegative(),
  reviewSummary: z.object({
    unmappedObservationCount: z.number().int().nonnegative(),
    ambiguousAliasGroupCount: z.number().int().nonnegative(),
  }).strict(),
  contractVersion: z.string().nullish(),
  availableFilters: AvailableFiltersSchema.nullish(),
  providerSlots: z.array(ProviderSlotSchema).nullish(),
  rows: z.array(InventoryRowSchema).optional().default([]),
  rowPopulationTotal: z.number().int().nonnegative().optional().default(0),
  withPhotosPopulation: z.number().int().nonnegative().optional().default(0),
  populationCounters: InventoryPopulationCountersSchema.nullish(),
  stockMismatchCount: z.number().int().nonnegative().optional().default(0),
  descriptionPresence: DescriptionPresenceSchema.nullish(),
  readyCounts: z.record(z.string(), z.number().int().nonnegative()).optional().default({}),
  storefrontTargets: z.array(z.string()).optional().default([]),
  // What the list would total per `panelCount` key, when the read asked for any.
  panelCounts: z.record(z.string(), z.number().int().nonnegative()).nullish(),
}).strict()

// The reader's own reasons. The allocation reason it used to duplicate here now
// belongs to the allocation vocabulary below, which the server composes from
// durable records rather than from a placeholder.
export const UnitReaderReasonSchema = z.enum([
  'unit.source_account_not_recorded',
  'unit.source_time_not_recorded',
  'unit.source_identity_not_recorded',
  'unit.warehouse_receipt_not_recorded',
  'unit.condition_clearance_not_recorded',
  'unit.demand_unit_relation_not_recorded',
  'unit.aggregate_observation_not_unit_identity',
  'unit.parcel_not_unit_identity',
  'unit.parcel_source_relation_not_recorded',
  'unit.parcel_source_relation_contested',
  'unit.parcel_account_binding_not_recorded',
  'unit.demand_reason_unrecognized',
  'unit.listing_target_grain_not_decided',
])

// Receipt, condition and eligibility are three independent answers, so each has
// its own closed set. A carrier arrival and a commercial cancellation are named
// refusals rather than absent values: both look like receipt and neither is one.
export const UnitReceiptStateSchema = z.enum(['authorized_warehouse_receipt', 'not_established'])
export const UnitReceiptReasonSchema = z.enum([
  'unit.receipt_attestation_policy_unresolved',
  'unit.carrier_arrival_is_not_warehouse_receipt',
  'unit.commercial_event_is_not_warehouse_receipt',
  'unit.aggregate_balance_is_not_a_piece',
])
export const UnitConditionStateSchema = z.enum(['cleared', 'not_assessed'])
export const UnitConditionReasonSchema = z.enum([
  'unit.condition_assessment_policy_unresolved',
  'unit.carrier_state_is_not_condition_clearance',
])
// Allocation is now read from durable records, so it has five answers rather than
// two. `not_recorded` and `unallocated` are deliberately different members: the
// first says nothing has ever been recorded about the piece, the second says the
// records were read and nothing holds it.
export const UnitAllocationStateSchema = z.enum([
  'unallocated',
  'allocated',
  'conflicted',
  'needs_review',
  'not_recorded',
])
export const UnitAllocationReasonSchema = z.enum([
  'unit.allocation_not_recorded',
  'unit.held_by_existing_allocation',
  'unit.held_by_accepted_remote_demand',
  'unit.conflicting_allocation_evidence',
  'unit.allocation_awaiting_review',
])
// Whether one piece is free of a recorded obligation. A different question from
// eligibility, which is gated on owner decisions; both come from the server and
// the browser decides neither.
export const UnitAvailabilityStateSchema = z.enum(['available', 'unavailable', 'undecidable'])
// Whether the allocation records cover this piece at all, published beside the
// state rather than folded into it: a sold piece is definitely unavailable even
// when nothing was ever recorded about its allocation.
export const UnitAllocationCoverageSchema = z.enum(['recorded', 'not_recorded'])
export const UnitAvailabilityBlockerSchema = z.enum([
  'availability.unit_lifecycle_sold',
  'availability.unit_lifecycle_in_transit',
  'availability.own_allocation_holds_unit',
  'availability.remote_demand_holds_unit',
  'availability.allocation_conflict',
  'availability.allocation_needs_review',
  'availability.allocation_coverage_not_recorded',
])
// Why an allocation transition happened. The browser renders these; it derives
// none of them, and an event that opens a review is identified by its reason.
export const UnitAllocationProvenanceSchema = z.enum([
  'provenance.own_admission',
  'provenance.bound_remote_demand',
  'provenance.contested_remote_candidates',
  'provenance.multiple_obligations',
  'provenance.open_review',
])

// One durable record naming a piece. `reference` is a LOCAL identity namespaced
// by kind, so the browser never has to parse it to know what it points at and no
// marketplace order key or producer key travels here. Three clocks, because there
// are three facts: what an outside party says happened, when we received it, and
// when the row was written. `current` is false for a record kept as evidence that
// no longer holds the piece.
// Anchored at both ends and matched against the ACTUAL local forms. A prefix
// check alone accepts `allocation:` followed by anything, so prefixing raw
// producer or order material would satisfy it — which is the entire thing the
// opaque reference exists to prevent.
export const LOCAL_RECORD_REFERENCE = /^(?:allocation:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|observation:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|event:[1-9][0-9]*)$/

export const AllocationRecordSchema = z.object({
  provenance: UnitAllocationProvenanceSchema,
  reference: z.string().regex(LOCAL_RECORD_REFERENCE),
  current: z.boolean(),
  occurredAt: timestamp.nullable(),
  receivedAt: timestamp,
  recordedAt: timestamp,
  relationRecordedAt: timestamp.nullable(),
  message: z.string().min(1),
}).strict()

export const AllocationReasonSchema = z.enum([
  'allocation.admitted_under_reviewed_policy',
  'allocation.released_by_caller',
  'allocation.expired_at_recorded_deadline',
  'allocation.payment_confirmed',
  'allocation.payment_declined',
  'allocation.payment_declined_after_release',
  'allocation.late_success_after_release',
  'allocation.confirmation_after_recorded_deadline',
  'allocation.contradictory_payment_evidence',
  'allocation.ambiguous_payment_evidence',
  'allocation.ambiguous_evidence_timing',
  'allocation.operator_correction_recorded',
  'allocation.operator_review_acknowledged',
])
export const UnitEligibilityStateSchema = z.enum(['eligible', 'ineligible', 'undecidable'])
export const UnitEligibilityBlockerSchema = z.union([
  UnitReceiptReasonSchema,
  UnitConditionReasonSchema,
  UnitAllocationReasonSchema,
  z.enum([
    'unit.protection_scope_binding_unresolved',
    'unit.marking_absence_restriction_unresolved',
  ]),
])
export const UnitPolicyGateSchema = z.enum([
  'unit.policy.receipt_condition_actor_and_correction_evidence',
  'unit.policy.marking_absence_restrictions',
  'unit.policy.onec_information_base_account_continuity',
  'unit.policy.provider_account_sales_source_mapping',
  'unit.policy.listing_target_grain',
])

export const IdentifiedUnitSchema = z.object({
  productId: z.string().uuid(),
  uin: z.string().nullable(),
  uinUnavailableReason: z.string().nullable(),
  provenance: z.enum(['reviewed_enrichment_decision', 'source_import_evidence']),
  // The source's own visible series code, never a key, so a piece that has none
  // carries its typed absence rather than an empty string.
  unitSeries: z.string().nullable(),
  unitSeriesUnavailableReason: UnitReaderReasonSchema.nullable(),
  providerCode: z.string().nullable(),
  accountCode: z.string().nullable(),
  sourceIdentityUnavailableReason: UnitReaderReasonSchema.nullable(),
  sourceTime: timestamp.nullable(),
  sourceTimeUnavailableReason: UnitReaderReasonSchema.nullable(),
  observedAt: timestamp,
  lifecycle: UnitLifecycleSchema,
  lifecycleReason: z.string().nullable(),
  custodyState: z.literal('unverified'),
  custodyReason: UnitReaderReasonSchema,
  allocationState: UnitAllocationStateSchema,
  allocationReason: UnitAllocationReasonSchema.nullable(),
  availabilityState: UnitAvailabilityStateSchema,
  availabilityCoverage: UnitAllocationCoverageSchema,
  availabilityBlockers: z.array(UnitAvailabilityBlockerSchema),
  allocationProvenance: z.array(UnitAllocationProvenanceSchema),
  // Server-owned Russian. The browser validates that each sentence is present and
  // non-empty; it never produces one from the codes beside it.
  allocationMessage: z.string().min(1),
  availabilityMessage: z.string().min(1),
  coverageMessage: z.string().min(1),
  allocationProvenanceMessages: z.array(z.string().min(1)),
  allocationRecords: z.array(AllocationRecordSchema),
  allocationEvidenceMessages: z.array(z.string().min(1)),
  allocationEvidenceCount: z.number().int().nonnegative(),
  allocationEvidenceReasons: z.array(AllocationReasonSchema),
  // The newest unresolved opening event's LOCAL identity, in the same closed set
  // of forms a record reference uses. It carried the producer's own evidence key,
  // which made it a second public route to what the record reference had just
  // stopped publishing.
  allocationEvidenceReference: z.string().regex(LOCAL_RECORD_REFERENCE).nullable(),
  allocationEvidenceSuppliedAt: timestamp.nullable(),
  allocationEvidenceReceivedAt: timestamp.nullable(),
  markingState: z.enum(['uin_recorded', 'not_recorded']),
  receiptState: UnitReceiptStateSchema,
  receiptReason: UnitReceiptReasonSchema.nullable(),
  conditionState: UnitConditionStateSchema,
  conditionReason: UnitConditionReasonSchema.nullable(),
  eligibilityState: UnitEligibilityStateSchema,
  eligibilityBlockers: z.array(UnitEligibilityBlockerSchema),
  listingTargetState: z.literal('unavailable'),
  listingTargetUnavailableReason: UnitReaderReasonSchema,
  weightState: z.enum(['recorded', 'unavailable']),
  weightGrams: money,
  weightUnavailableReason: z.string().nullable(),
}).strict().superRefine((unit, context) => {
  // The server pairs the marking with its reason under a database CHECK. The
  // browser restates the pair rather than trusting it, because a response that
  // carried both or neither would render an empty marking that means nothing.
  if ((unit.markingState === 'uin_recorded') !== (unit.uin !== null)) {
    context.addIssue({ code: 'custom', path: ['uin'], message: 'A recorded marking carries a UIN and an unrecorded one does not' })
  }
  if ((unit.uin === null) !== (unit.uinUnavailableReason !== null)) {
    context.addIssue({ code: 'custom', path: ['uinUnavailableReason'], message: 'A unit carries either a UIN or a typed reason' })
  }
  if (unit.eligibilityState === 'eligible' && unit.eligibilityBlockers.length > 0) {
    context.addIssue({ code: 'custom', path: ['eligibilityBlockers'], message: 'An eligible unit carries no blocking reason' })
  }
  if (unit.eligibilityState !== 'eligible' && unit.eligibilityBlockers.length === 0) {
    context.addIssue({ code: 'custom', path: ['eligibilityBlockers'], message: 'A unit that is not eligible states why' })
  }
  // The server pairs the allocation state with its reason. Restated here so a
  // response that said `not_recorded` with no reason, or `unallocated` with one,
  // cannot reach a cell that would have to guess what it meant.
  if ((unit.allocationState === 'unallocated') !== (unit.allocationReason === null)) {
    context.addIssue({ code: 'custom', path: ['allocationReason'], message: 'An unallocated unit names no reason and every other state names one' })
  }
  if (unit.availabilityState === 'available' && unit.availabilityBlockers.length > 0) {
    context.addIssue({ code: 'custom', path: ['availabilityBlockers'], message: 'An available unit carries no blocker' })
  }
  if (unit.availabilityState === 'unavailable' && unit.availabilityBlockers.length === 0) {
    context.addIssue({ code: 'custom', path: ['availabilityBlockers'], message: 'An unavailable unit states why' })
  }
  // One obligation is reported once per piece. A repeated blocker would be the
  // same piece subtracted twice, which is the arithmetic this contract refuses.
  if (new Set(unit.availabilityBlockers).size !== unit.availabilityBlockers.length) {
    context.addIssue({ code: 'custom', path: ['availabilityBlockers'], message: 'One obligation is reported once per unit' })
  }
  if ((unit.availabilityCoverage === 'recorded') !== (unit.allocationState !== 'not_recorded')) {
    context.addIssue({ code: 'custom', path: ['availabilityCoverage'], message: 'Coverage states whether an allocation record exists' })
  }
  // Absent evidence stays typed absent. Borrowing `observedAt`, which is when the
  // PIECE was recorded, would answer a different question with the same number.
  const hasEvidence = unit.allocationEvidenceCount > 0
  if (hasEvidence !== (unit.allocationEvidenceReasons.length > 0)) {
    context.addIssue({ code: 'custom', path: ['allocationEvidenceReasons'], message: 'Unresolved evidence names its reasons' })
  }
  if (!hasEvidence && (unit.allocationEvidenceReference !== null || unit.allocationEvidenceSuppliedAt !== null || unit.allocationEvidenceReceivedAt !== null)) {
    context.addIssue({ code: 'custom', path: ['allocationEvidenceReceivedAt'], message: 'A unit with no unresolved evidence carries no evidence clock' })
  }
  if (hasEvidence && unit.allocationEvidenceReceivedAt === null) {
    context.addIssue({ code: 'custom', path: ['allocationEvidenceReceivedAt'], message: 'Unresolved evidence carries the server receipt clock' })
  }
  if (unit.allocationEvidenceMessages.length !== unit.allocationEvidenceReasons.length) {
    context.addIssue({ code: 'custom', path: ['allocationEvidenceMessages'], message: 'Every evidence reason carries exactly one sentence' })
  }
  // Provenance says which durable records put the piece where it is. Only a
  // `not_recorded` piece and a positively free one name none, because those are
  // the two cases where there is nothing recorded to name.
  if (new Set(unit.allocationProvenance).size !== unit.allocationProvenance.length) {
    context.addIssue({ code: 'custom', path: ['allocationProvenance'], message: 'One kind of record is reported once per unit' })
  }
  if (unit.allocationProvenanceMessages.length !== unit.allocationProvenance.length) {
    context.addIssue({ code: 'custom', path: ['allocationProvenanceMessages'], message: 'Every provenance member carries exactly one sentence' })
  }
  const silent = unit.allocationState === 'not_recorded' || unit.allocationState === 'unallocated'
  if (unit.allocationProvenance.length === 0 && !silent) {
    context.addIssue({ code: 'custom', path: ['allocationProvenance'], message: 'A held, conflicted or reviewed unit names its records' })
  }
  if (unit.allocationProvenance.length > 0 && silent) {
    context.addIssue({ code: 'custom', path: ['allocationProvenance'], message: 'A unit with no obligation names no record' })
  }
  // Every published record kind is backed by a CURRENT record, and every current
  // record belongs to a published kind. `multiple_obligations` is excluded because
  // it is a relation BETWEEN records; no single row can carry it.
  //
  // Written as an explicit predicate because the two sides are different sets: the
  // published kinds narrow to what a record may carry, while a record's own
  // provenance stays the full aggregate union, so a row naming the relation kind is
  // reported rather than silently dropped before the comparison.
  const isRecordKind = (
    member: UnitAllocationProvenance,
  ): member is Exclude<UnitAllocationProvenance, 'provenance.multiple_obligations'> =>
    member !== 'provenance.multiple_obligations'
  const claimed = new Set(unit.allocationProvenance.filter(isRecordKind))
  const carried = new Set(unit.allocationRecords.filter((record) => record.current).map((record) => record.provenance))
  for (const member of carried) {
    if (!isRecordKind(member) || !claimed.has(member)) {
      context.addIssue({ code: 'custom', path: ['allocationRecords'], message: 'A record names a provenance the unit does not publish' })
    }
  }
  for (const member of claimed) {
    if (!carried.has(member)) {
      context.addIssue({ code: 'custom', path: ['allocationRecords'], message: 'A published record kind names no actual record' })
    }
  }
  // A completed record is evidence that the records were READ and nothing holds
  // the piece, which is a different answer from never having looked. It belongs
  // only to `unallocated`, there is at most one, and it is never a provenance
  // member - an operator reading it as an active hold is the failure to prevent.
  const completed = unit.allocationRecords.filter((record) => !record.current)
  if (completed.length > 0 && unit.allocationState !== 'unallocated') {
    context.addIssue({ code: 'custom', path: ['allocationRecords'], message: 'Only a free unit keeps a completed record' })
  }
  if (completed.length > 1) {
    context.addIssue({ code: 'custom', path: ['allocationRecords'], message: 'A free unit keeps at most one completed record' })
  }
  // Always an own admission: an accepted order line and an open review are not
  // things this projection keeps once they end, so a completed one of either is a
  // shape the server cannot produce.
  if (completed.some((record) => record.provenance !== 'provenance.own_admission')) {
    context.addIssue({ code: 'custom', path: ['allocationRecords'], message: 'A completed record is always an own admission' })
  }
  if (new Set(unit.allocationRecords.map((record) => record.reference)).size !== unit.allocationRecords.length) {
    context.addIssue({ code: 'custom', path: ['allocationRecords'], message: 'Every record is identified once' })
  }
  // Two rows carry a remote record — the observation and the unit relation — so a
  // bound or contested record states when the second was written. An own admission
  // and a review event are single rows; a relation clock on either would be a fact
  // about a row that does not exist.
  const remoteKinds = new Set(['provenance.bound_remote_demand', 'provenance.contested_remote_candidates'])
  for (const record of unit.allocationRecords) {
    if (remoteKinds.has(record.provenance) !== (record.relationRecordedAt !== null)) {
      context.addIssue({ code: 'custom', path: ['allocationRecords'], message: 'A remote record states when its relation was recorded, and no other record carries one' })
    }
  }
})

export const UnresolvedPopulationSchema = z.object({
  id: z.string(),
  kind: z.enum(['demand', 'parcel', 'aggregate_observation']),
  productId: z.string().uuid().nullable(),
  providerCode: z.string().nullable(),
  accountCode: z.string().nullable(),
  sourceId: z.string().nullable(),
  externalOrderId: z.string().nullable(),
  sourceIdentityUnavailableReason: UnitReaderReasonSchema.nullable(),
  sourceTime: timestamp.nullable(),
  sourceTimeUnavailableReason: UnitReaderReasonSchema.nullable(),
  observedAt: timestamp,
  quantity: z.number().int().nonnegative().nullable(),
  reason: z.union([UnitReaderReasonSchema, GuardReasonSchema]),
  custodyState: z.literal('unverified'),
  custodyReason: UnitReaderReasonSchema,
  parcelReference: z.string().nullable(),
  transportDirection: z.enum(['outbound', 'return']).nullable(),
  transportState: z.enum([
    'preparing',
    'in_transit',
    'ready_for_pickup',
    'out_for_delivery',
    'delivery_issue',
    'delivered',
    'returned',
    'cancelled',
  ]).nullable(),
  transportStatusLabel: z.string().nullable(),
  transportNeedsReview: z.boolean().nullable(),
  parcelAssignmentState: z.enum(['resolved', 'unresolved', 'contested']).nullable(),
  parcelAssignmentEvidence: z.enum(['order_association', 'shipment_document', 'provider_event']).nullable(),
  parcelAssignmentReason: UnitReaderReasonSchema.nullable(),
  receiptState: UnitReceiptStateSchema,
  receiptReason: UnitReceiptReasonSchema,
  conditionState: UnitConditionStateSchema,
  conditionReason: UnitConditionReasonSchema,
}).strict()

export const PopulationWindowSchema = z.object({
  returnedCount: z.number().int().nonnegative(),
  limit: z.number().int().positive(),
  truncated: z.boolean(),
  availability: z.enum(['available', 'unavailable']),
  unavailableReason: z.enum([
    'unit.parcel_account_mapping_unavailable',
    'unit.parcel_variant_relation_unavailable',
  ]).nullable(),
}).strict().superRefine((window, context) => {
  if (window.availability === 'available' && window.unavailableReason !== null) {
    context.addIssue({ code: 'custom', path: ['unavailableReason'], message: 'Available population cannot carry an unavailable reason' })
  }
  if (window.availability === 'unavailable' && window.unavailableReason === null) {
    context.addIssue({ code: 'custom', path: ['unavailableReason'], message: 'Unavailable population requires a typed reason' })
  }
})

export const PopulationWindowsSchema = z.object({
  identifiedUnits: PopulationWindowSchema,
  demand: PopulationWindowSchema,
  parcels: PopulationWindowSchema,
  aggregateObservations: PopulationWindowSchema,
}).strict()

// Why a keyed variant read could not answer. Closed, and in the server's member
// order. The browser validates this and renders `message`; it never maps a reason
// to Russian text of its own, because two independent classifications of one fact
// is how one state ends up described two different ways on two screens.
export const InventoryVariantUnavailableReasonSchema = z.enum([
  'frozen_identity.not_found',
  'frozen_identity.projection_incomplete',
  'frozen_identity.ambiguous_identity',
  'frozen_identity.not_in_latest_snapshot',
  'frozen_identity.ambiguous_snapshot',
])

export const InventoryVariantUnavailableSchema = z.object({
  // Read leniently against InventoryVariantUnavailableReasonSchema. The
  // sentence is the server's; refusing an unrecognised code would replace an
  // explained refusal with a parse failure.
  reason: z.string(),
  message: z.string().min(1).max(240),
}).strict()

export const ProductMediaKindSchema = z.enum(['image', 'video', 'instruction', 'unknown'])
export const ProductMediaStatusSchema = z.enum(['pending', 'downloaded', 'failed', 'unknown'])
export const ProductMediaUnavailableReasonSchema = z.enum([
  'product_media.not_recorded',
  'product_media.download_pending',
  'product_media.download_failed',
  'product_media.asset_unresolved',
  'product_media.status_unknown',
])
// Why one row carries no probe measurement. The first four are what
// `media_asset.probe_unavailable_reason` stores; the last three are the read
// path's own, for absences that column cannot express.
export const ProductMediaProbeUnavailableReasonSchema = z.enum([
  'media.prober_absent',
  'image_probe.input_limit',
  'image_probe.header_truncated',
  'image_probe.header_unrecognized',
  'image_probe.content_type_not_probed',
  'media_probe.asset_absent',
  'media_probe.reason_not_recorded',
  'media_probe.reason_unknown',
])

// `mediaUrl` and `unavailableReason` are exclusive, and the message travels with
// the reason. The schema refuses a body carrying both or neither rather than
// letting a tile render blank, which is indistinguishable from a broken page.
export const ProductMediaItemSchema = z.object({
  // The `product_media` row. `PUT /api/products/{productId}/media/{mediaId}/primary`
  // takes this id, so it is the only thing that lets a tile address itself.
  id: z.string().uuid(),
  kind: ProductMediaKindSchema,
  position: z.number().int().positive(),
  // The server's stored decision. Selecting a primary repositions it to 1, so
  // the two normally coincide — which is exactly why nothing here may read
  // `position === 1` as the answer. The flag is the only source.
  isPrimary: z.boolean(),
  status: ProductMediaStatusSchema,
  mediaUrl: z.string().nullable(),
  unavailableReason: ProductMediaUnavailableReasonSchema.nullable(),
  unavailableMessage: z.string().min(1).max(240).nullable(),
  failureReason: z.string().nullable(),
  source: z.string().min(1),
  uploadedBy: z.string().nullable(),
  // Pixels and seconds, never a rounded aspect string: a ratio cannot be
  // un-rounded, and the tile crop, the lightbox and the next consumer each want
  // something different from the same two numbers.
  widthPx: z.number().int().nullable(),
  heightPx: z.number().int().nullable(),
  durationSeconds: decimalOnTheWire,
  probeUnavailableReason: ProductMediaProbeUnavailableReasonSchema.nullable(),
  // Server-owned Russian, capped exactly like `unavailableMessage` and carried
  // with `probeUnavailableReason`. The lightbox prints this sentence; nothing
  // here maps a probe reason to text of its own.
  probeUnavailableMessage: z.string().min(1).max(240).nullable(),
}).strict().refine(
  (item) => (item.mediaUrl === null) !== (item.unavailableReason === null),
  { message: 'mediaUrl and unavailableReason are exclusive' },
).refine(
  (item) => (item.unavailableReason === null) === (item.unavailableMessage === null),
  { message: 'unavailableReason and unavailableMessage travel together' },
).refine(
  // `probe_value_xor_reason` is one rule over the three measurements together,
  // so this mirrors it as a group. A photo therefore carries dimensions, a null
  // duration and a null reason: the table's answer, not a dropped field.
  (item) => (
    item.widthPx === null && item.heightPx === null && item.durationSeconds === null
  ) === (item.probeUnavailableReason !== null),
  { message: 'the probe measurements and probeUnavailableReason are exclusive' },
).refine(
  (item) => (item.probeUnavailableReason === null) === (item.probeUnavailableMessage === null),
  { message: 'probeUnavailableReason and probeUnavailableMessage travel together' },
)

export const ProductMediaSchema = z.object({
  // Media is owned by the product, and `productId` is what every write and
  // byte route takes, so the piece is the whole address of this surface.
  productId: z.string().uuid(),
  items: z.array(ProductMediaItemSchema),
  total: z.number().int().nonnegative(),
  imageTotal: z.number().int().nonnegative(),
  videoTotal: z.number().int().nonnegative(),
  merchandisingTotal: z.number().int().nonnegative(),
  otherFilesTotal: z.number().int().nonnegative(),
  unavailableReason: ProductMediaUnavailableReasonSchema.nullable(),
  unavailableMessage: z.string().min(1).max(240).nullable(),
}).strict().refine(
  (media) => (media.items.length === 0) === (media.unavailableReason !== null),
  { message: 'an empty media list must name its reason, and only then' },
).refine(
  (media) => media.total === media.items.length
    && media.merchandisingTotal === media.imageTotal + media.videoTotal
    && media.total === media.merchandisingTotal + media.otherFilesTotal,
  { message: 'media population totals must describe the server-owned set' },
)

// Why a Product-addressed read named no subject. Deliberately its own closed
// set: "we never had this product" and "this product has no legacy variant" are
// different facts, and only the first is a refusal.
export const InventoryProductNotFoundReasonSchema = z.enum(['product.not_recorded'])

export const InventoryProductNotFoundSchema = z.object({
  reason: InventoryProductNotFoundReasonSchema,
  message: z.string().min(1).max(240),
}).strict()

// Mirrors `forge/inventory/order_history.py`. `unrecognized` is the server's
// fallback for a provider token it was not told about; the token never arrives.
export const ProductOrderStatusSchema = z.enum(['delivered', 'cancelled', 'in_delivery', 'processing', 'unrecognized'])
export const OrderHistoryUnavailableReasonSchema = z.enum([
  'order.customer_name_not_recorded',
  'order.city_not_recorded',
])

type RecordedTextFields = {
  value: string | null
  reason: z.infer<typeof OrderHistoryUnavailableReasonSchema> | null
  message: string | null
}

function isValueOrReason({ value, reason, message }: RecordedTextFields): boolean {
  return (value === null) !== (reason === null) && (reason === null) === (message === null)
}

export const ProductOrderHistoryItemSchema = z.object({
  orderedAt: z.iso.datetime({ offset: true }),
  // The sales source's provider code, named through the storefront registry.
  marketplace: z.string().min(1),
  externalOrderId: z.string().min(1),
  status: ProductOrderStatusSchema,
  // The related order line's amount, not the whole order's total.
  lineAmount: z.object({
    amount: z.union([z.number(), z.string().regex(/^-?\d+(?:\.\d+)?$/).transform(Number)]),
    currency: z.string().min(1),
  }).strict(),
  customerName: z.string().min(1).nullable(),
  customerNameUnavailableReason: OrderHistoryUnavailableReasonSchema.nullable(),
  customerNameUnavailableMessage: z.string().min(1).max(240).nullable(),
  city: z.string().min(1).nullable(),
  cityUnavailableReason: OrderHistoryUnavailableReasonSchema.nullable(),
  cityUnavailableMessage: z.string().min(1).max(240).nullable(),
}).strict().refine(
  (order) => isValueOrReason({
    value: order.customerName,
    reason: order.customerNameUnavailableReason,
    message: order.customerNameUnavailableMessage,
  }),
  { message: 'customerName carries exactly one of a value and a reason with its message' },
).refine(
  (order) => isValueOrReason({ value: order.city, reason: order.cityUnavailableReason, message: order.cityUnavailableMessage }),
  { message: 'city carries exactly one of a value and a reason with its message' },
)

export const ProductOrderHistorySchema = z.object({
  productId: z.string().uuid(),
  items: z.array(ProductOrderHistoryItemSchema),
  total: z.number().int().nonnegative(),
}).strict().refine(
  (history) => history.total === history.items.length,
  { message: 'the order history total counts the published orders' },
)

export type ProductOrderStatus = z.infer<typeof ProductOrderStatusSchema>
export type ProductOrderHistoryItem = z.infer<typeof ProductOrderHistoryItemSchema>
export type ProductOrderHistory = z.infer<typeof ProductOrderHistorySchema>

export const CardSeriesLinkageUnavailableReasonSchema = z.enum([
  'unit.card_series_extract_evidence_not_available',
])

export const ProductMediaWriteSchema = z.object({
  id: z.string().uuid(),
  productId: z.string().uuid(),
  kind: ProductMediaKindSchema,
  position: z.number().int().positive(),
  isPrimary: z.boolean(),
  source: z.string(),
  sourceUrl: z.string().nullable(),
  status: ProductMediaStatusSchema,
  sha256: z.string().regex(/^[0-9a-f]{64}$/),
  mediaUrl: z.string().min(1),
  uploadedAt: z.string().nullable(),
  uploadedBy: z.string().nullable(),
}).strict()

export const ProductMediaOrderItemSchema = z.object({
  id: z.string().uuid(),
  kind: ProductMediaKindSchema,
  position: z.number().int().positive(),
  isPrimary: z.boolean(),
  status: ProductMediaStatusSchema,
}).strict()

export const ProductMediaOrderSchema = z.object({
  productId: z.string().uuid(),
  items: z.array(ProductMediaOrderItemSchema),
}).strict()

export const UnitWarehouseSchema = z.object({
  identifiedUnits: z.array(IdentifiedUnitSchema),
  unresolvedPopulations: z.array(UnresolvedPopulationSchema),
  populationWindows: PopulationWindowsSchema,
  openPolicyGates: z.array(UnitPolicyGateSchema),
  cardSeriesLinkageAvailability: z.literal('unavailable'),
  cardSeriesLinkageUnavailableReason: CardSeriesLinkageUnavailableReasonSchema,
}).strict()

// The ungrouped Warehouse table. One row is one physical piece; the server
// decides the order and the scope and echoes both, so the client sorts nothing
// and can draw the applied lifecycle as a chip it is able to clear.
export const UnitListingSortSchema = z.enum(['uin', 'article', 'title', 'weight', 'lifecycle'])
// One direction grammar for every ordered inventory read, defined once so the
// two routes cannot drift into two spellings of the same word.
export const InventorySortDirectionSchema = z.enum(['asc', 'desc'])
export const UnitListingDirectionSchema = InventorySortDirectionSchema
// The order level's own sortable columns (`/groups?level=order`); absent is newest first.
export const InventoryOrderGroupSortSchema = z.enum(['order_number', 'order_created_at', 'buyer', 'order_status', 'city', 'order_amount'])
// The Warehouse page's own sortable columns. `movement` is the piece's last
// business movement and is the server's default, newest first; the browser sorts
// nothing, so a column missing from this set has no sortable header.
export const InventoryPageSortSchema = z.enum([
  'uin',
  'article',
  'title',
  'weight',
  'lifecycle',
  'size',
  'photo_count',
  'movement',
  'onec_price',
  'storefront_price_wildberries',
  'storefront_price_ymarket',
  'storefront_price_ozon',
  'storefront_price_ori_site',
  'ymarket_sells_for',
  'ymarket_promo',
  'ymarket_boost',
  'ymarket_price_per_gram',
  'ymarket_purchase_cost',
  'ymarket_earnings',
  'ymarket_earnings_percent',
  'ymarket_showcase',
  'ymarket_showcase_per_gram',
  'ymarket_showcase_threshold_ratio',
  'ymarket_net_per_gram',
  'ymarket_price_for_target',
])
export const UnitLifecycleScopeSchema = z.enum(['all', 'in_stock', 'in_transit', 'sold'])
export const InventoryGroupReasonSchema = z.enum([
  'group.article_not_recorded',
  'group.tech_size_not_recorded',
  'group.stock_observation_absent',
  // Reported, but at least one contributing row carried no number, so any sum
  // is a subtotal. Different from the line above and never rendered as 0.
  'group.quantity_not_reported',
  'group.unit_weight_not_recorded',
  'group.no_multi_member_confirmed_model',
  'group.frozen_registry_row_not_grouped',
  // A refusal rather than an absence: the group key was not one this server
  // produced. It rides in this family because the client renders it the same way.
  'group.key_not_recognized',
  'group.reason_not_published',
])
export const CostUnavailableReasonSchema = z.enum(['cost_not_known', 'cost_not_recorded', 'cost_invalid', 'cost_conflicting_rows', 'receipt_provenance_missing', 'receipt_not_found', 'receipt_quantity_not_one', 'receipt_cost_is_retail_price'])

export const UnitListingRowSchema = z.object({
  productId: z.string().uuid(),
  modelId: z.string().uuid().nullable(),
  // ModelGroupingReasonSchema, read leniently.
  modelUnavailableReason: z.string().nullable(),
  uin: z.string().nullable(),
  // WarehouseIdentityReasonSchema on all three, read leniently.
  uinUnavailableReason: z.string().nullable(),
  // The source's own visible series code, renumbered between captures and
  // never a key, so a piece that has none carries its typed absence.
  unitSeries: z.string().nullable(),
  unitSeriesUnavailableReason: z.string().nullable(),
  // The piece has no reason column beside its size, so there is no pair here.
  size: z.string().nullable(),
  weightGrams: money,
  // WarehouseFactReasonSchema on all three, read leniently.
  weightUnavailableReason: z.string().nullable(),
  cost: money,
  costUnavailableReason: CostUnavailableReasonSchema.nullable(),
  costSourceDocumentNumber: z.string().nullable(),
  costReceiptDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable(),
  costSyncedAt: z.iso.datetime({ offset: true }).nullable(),
  costSyncRunId: z.string().regex(/^[0-9a-f]{64}$/).nullable(),
  // The state 1C recorded on the piece, or the reason it recorded none.
  lifecycle: UnitLifecycleSchema.nullable(),
  lifecycleUnavailableReason: WarehouseFactReasonSchema.nullable(),
  article: z.string().nullable(),
  articleUnavailableReason: z.string().nullable(),
  title: z.string().nullable(),
  titleUnavailableReason: z.string().nullable(),
  material: z.string().nullable(),
  materialUnavailableReason: z.string().nullable(),
  fineness: z.string().nullable(),
  finenessUnavailableReason: z.string().nullable(),
  primaryMediaUrl: z.string().nullable(),
  primaryMediaUnavailableReason: ProductMediaUnavailableReasonSchema.nullable(),
  channelPresence: z.array(MarketplacePresenceSchema),
  reconciliationState: InventoryReconciliationStateSchema,
  problems: z.array(InventoryProblemSchema).optional().default([]),
  problemCount: z.number().int().nonnegative().optional().default(0),
  problemsAvailable: z.boolean().optional().default(false),
  problemsUnavailableReason: z.string().nullable().optional().default(null),
}).strict().superRefine((row, context) => {
  const pairs: [string, unknown, unknown][] = [
    ['uin', row.uin, row.uinUnavailableReason],
    ['weightGrams', row.weightGrams, row.weightUnavailableReason],
    ['cost', row.cost, row.costUnavailableReason],
    ['lifecycle', row.lifecycle, row.lifecycleUnavailableReason],
    ['article', row.article, row.articleUnavailableReason],
    ['title', row.title, row.titleUnavailableReason],
    ['material', row.material, row.materialUnavailableReason],
    ['fineness', row.fineness, row.finenessUnavailableReason],
    ['modelId', row.modelId, row.modelUnavailableReason],
    ['primaryMediaUrl', row.primaryMediaUrl, row.primaryMediaUnavailableReason],
  ]
  for (const [name, value, reason] of pairs) {
    if ((value === null) !== (reason !== null)) {
      context.addIssue({
        code: 'custom',
        path: [name],
        message: `${name} carries either a value or a typed reason, never both or neither`,
      })
    }
  }
  if (row.cost !== null && (
    row.costSourceDocumentNumber === null ||
    row.costReceiptDate === null ||
    row.costSyncedAt === null ||
    row.costSyncRunId === null
  )) {
    context.addIssue({ code: 'custom', path: ['cost'], message: 'cost requires complete 1C receipt provenance' })
  }
  if (row.problemCount !== row.problems.length) {
    context.addIssue({ code: 'custom', path: ['problemCount'], message: 'problemCount must equal problems.length' })
  }
})

export const UnitListingPageSchema = z.object({
  items: z.array(UnitListingRowSchema),
  total: z.number().int().nonnegative(),
  limit: z.number().int().positive(),
  offset: z.number().int().nonnegative(),
  sort: UnitListingSortSchema,
  direction: UnitListingDirectionSchema,
  lifecycle: UnitLifecycleScopeSchema,
  inStockQuantity: z.number().int().nullable(),
  inStockUnavailableReason: InventoryGroupReasonSchema.nullable(),
  markedUnitCount: z.number().int().nonnegative(),
  articleCount: z.number().int().nonnegative(),
  withPhotosCount: z.number().int().nonnegative(),
  contractVersion: z.string(),
}).strict().superRefine((value, context) => {
  if ((value.inStockQuantity === null) === (value.inStockUnavailableReason === null)) {
    context.addIssue({
      code: 'custom',
      path: ['inStockQuantity'],
      message: 'inStockQuantity carries either a value or a typed reason, never both or neither',
    })
  }
})

export type InventoryDiscrepancyReason = z.infer<typeof InventoryDiscrepancyReasonSchema>
export type BindingScope = z.infer<typeof BindingScopeSchema>
export type BindingState = z.infer<typeof BindingStateSchema>
export type SourceKind = z.infer<typeof SourceKindSchema>
export type FreshnessState = z.infer<typeof FreshnessStateSchema>
export type InventoryRowGrain = z.infer<typeof InventoryRowGrainSchema>
export type InventoryReviewState = z.infer<typeof InventoryReviewStateSchema>
export type InventoryStockState = z.infer<typeof InventoryStockStateSchema>
export type InventoryProductState = z.infer<typeof InventoryProductStateSchema>
export type InventoryMovementKind = z.infer<typeof InventoryMovementKindSchema>
export type InventoryReconciliationState = z.infer<typeof InventoryReconciliationStateSchema>
export type AvailabilityHoldState = z.infer<typeof AvailabilityHoldStateSchema>
export type WarehouseFactReason = z.infer<typeof WarehouseFactReasonSchema>
export type WarehouseIdentityReason = z.infer<typeof WarehouseIdentityReasonSchema>
export type ModelGroupingReason = z.infer<typeof ModelGroupingReasonSchema>
export type MarketplaceListingReason = z.infer<typeof MarketplaceListingReasonSchema>
export type SourceProjectionReason = z.infer<typeof SourceProjectionReasonSchema>
export type ComparableFieldReason = z.infer<typeof ComparableFieldReasonSchema>
export type FrozenMappingRefusalReason = z.infer<typeof FrozenMappingRefusalReasonSchema>
export type InventorySource = z.infer<typeof InventorySourceSchema>
export type StorefrontPrice = z.infer<typeof StorefrontPriceSchema>
export type ReadinessIssue = z.infer<typeof ReadinessIssueSchema>
export type StorefrontReadiness = z.infer<typeof StorefrontReadinessSchema>
export type InventoryVariant = z.infer<typeof InventoryVariantSchema>
export type InventoryListItem = z.infer<typeof InventoryListItemSchema>
export type ListSource = z.infer<typeof ListSourceSchema>
export type ListMarketplaceListing = z.infer<typeof ListMarketplaceListingSchema>
export type ListStockMismatchFinding = z.infer<typeof ListStockMismatchFindingSchema>
export type InventoryPage = z.infer<typeof InventoryPageSchema>
export type InventoryRow = z.infer<typeof InventoryRowSchema>
export type InventoryPopulationCounters = z.infer<typeof InventoryPopulationCountersSchema>
export type MarketplacePresenceState = z.infer<typeof MarketplacePresenceStateSchema>
export type ProviderConnectionState = z.infer<typeof ProviderConnectionStateSchema>
export type ListingStateSource = z.infer<typeof ListingStateSourceSchema>
export type FieldDiffState = z.infer<typeof FieldDiffStateSchema>
export type FieldAuthority = z.infer<typeof FieldAuthoritySchema>
export type ComparableField = z.infer<typeof ComparableFieldSchema>
export type MatchRule = z.infer<typeof MatchRuleSchema>
export type MatchReason = z.infer<typeof MatchReasonSchema>
export type MatchBucket = z.infer<typeof MatchBucketSchema>
export type MatchReadUnavailableReason = z.infer<typeof MatchReadUnavailableReasonSchema>
export type ProposalDecision = z.infer<typeof ProposalDecisionSchema>
// The grouped Warehouse projection. Grouping is a server query, so nothing here
// derives a group, a count or a level from rows the browser happens to hold.
export const InventoryGroupLevelSchema = z.enum(['model', 'article', 'articleSize', 'order'])
// Not the same set as the levels: `unit` is a leaf and no result groups by it.
export const InventoryGroupChildLevelSchema = z.enum(['article', 'articleSize', 'unit'])

export const GroupWeightSchema = z.object({
  // Never read the average without the range beside it: every piece has its own
  // grams, so the average alone is not a measurement of any ring.
  averageGrams: money,
  minGrams: money,
  maxGrams: money,
  // InventoryGroupReasonSchema, read leniently.
  unavailableReason: z.string().nullable(),
  unitCount: z.number().int().nonnegative(),
}).strict()

// Two attributed numbers a group could not reconcile: what 1C reports for it and
// how many Products we can actually name. Neither is the other's correction and
// they are never summed. `unnamedQuantity` is the difference, which is
// merchandise the source tracks only as a number.
export const GroupStockConflictKindSchema = z.enum([
  'group_stock.source_quantity_exceeds_products',
  'group_stock.shelf_and_sale_disagree',
  'group_stock.equal_timestamp_movement',
])
export const GroupStockConflictSchema = z.object({
  kind: GroupStockConflictKindSchema,
  sourceQuantity: z.number().int().nonnegative(),
  productCount: z.number().int().nonnegative(),
  unnamedQuantity: z.number().int().positive(),
}).strict()

export const InventoryGroupRowSchema = z.object({
  groupKey: z.string().min(1),
  level: InventoryGroupLevelSchema,
  childLevel: InventoryGroupChildLevelSchema,
  modelId: z.string().uuid().nullable(),
  modelArticle: z.string().nullable(),
  // ModelGroupingReasonSchema, read leniently.
  modelUnavailableReason: z.string().nullable(),
  article: z.string().nullable(),
  // InventoryGroupReasonSchema, read leniently.
  articleUnavailableReason: z.string().nullable(),
  techSize: z.string().nullable(),
  techSizeUnavailableReason: z.string().nullable(),
  title: z.string().nullable(),
  variantCount: z.number().int().nonnegative(),
  childCount: z.number().int().nonnegative(),
  // Physical pieces in the selected lifecycle scope. This is deliberately not
  // the 1C stock quantity or the number of child groups.
  unitCount: z.number().int().nonnegative(),
  // `На складе, шт`: a count of the member Products whose derived state puts them
  // in the warehouse. This is the stock figure, and it is the ONLY one. It is
  // never a quantity read from 1C.
  inStockQuantity: z.number().int().nullable(),
  inStockUnavailableReason: z.string().nullable(),
  reservedQuantity: z.number().int().nullable(),
  reservedUnavailableReason: z.string().nullable(),
  // Nullish and defaulted: a fixture that omits the key and a server that sends
  // an explicit null both parse, and the row schema is strict.
  stockConflict: GroupStockConflictSchema.nullish().default(null),
  // A second, separately labelled fact: how many of those pieces carry a УИН.
  // Rendered as `из них с УИН` and never in place of `inStockQuantity`.
  markedUnitCount: z.number().int().nonnegative(),
  weight: GroupWeightSchema,
  photoCount: z.number().int().nonnegative(),
  videoCount: z.number().int().nonnegative(),
  discrepancyCount: z.number().int().nonnegative(),
  reconciliationState: InventoryReconciliationStateSchema,
  orderNumber: z.string().nullable().optional().default(null),
  orderCreatedAt: timestamp.nullable().optional().default(null),
  buyer: z.string().nullable().optional().default(null),
  customerId: z.string().nullable().optional().default(null),
  orderStatus: z.string().nullable().optional().default(null),
  orderSubstatus: z.string().nullable().optional().default(null),
  program: z.string().nullable().optional().default(null),
  city: z.string().nullable().optional().default(null),
  orderAmount: money.optional().default(null),
  pieceCount: z.number().int().nonnegative().nullable().optional().default(null),
  lineCount: z.number().int().nonnegative().nullable().optional().default(null),
  composition: z.string().nullable().optional().default(null),
  problems: z.array(InventoryProblemSchema).optional().default([]),
  problemCount: z.number().int().nonnegative().optional().default(0),
  problemAssessmentUnavailableCount: z.number().int().nonnegative().optional().default(0),
  problemsAvailable: z.boolean().optional().default(false),
  problemsUnavailableReason: z.string().nullable().optional().default(null),
}).strict().superRefine((row, context) => {
  if (row.problemCount !== row.problems.length) {
    context.addIssue({ code: 'custom', path: ['problemCount'], message: 'problemCount must equal problems.length' })
  }
})

export const ModelLevelAvailabilitySchema = z.object({
  available: z.boolean(),
  // InventoryGroupReasonSchema, read leniently.
  unavailableReason: z.string().nullable(),
}).strict()

export const InventoryGroupPageSchema = z.object({
  level: InventoryGroupLevelSchema,
  items: z.array(InventoryGroupRowSchema),
  total: z.number().int().nonnegative(),
  limit: z.number().int().positive(),
  offset: z.number().int().nonnegative(),
  groupedVariantTotal: z.number().int().nonnegative(),
  ungroupedVariantTotal: z.number().int().nonnegative(),
  ungroupedVariantReason: z.string().nullable(),
  modelLevel: ModelLevelAvailabilitySchema,
  lifecycle: WarehouseStatusScopeSchema,
  descriptionPresence: DescriptionPresenceSchema.nullish(),
  populationRows: z.array(InventoryRowSchema).optional().default([]),
  populationRowTotal: z.number().int().nonnegative().optional().default(0),
  contractVersion: z.string().nullish(),
}).strict()

export const InventoryGroupUnitSchema = z.object({
  productId: z.string().uuid(),
  uin: z.string().nullable(),
  // WarehouseFactReasonSchema, read leniently. Exactly one of the pair is set.
  uinUnavailableReason: z.string().nullable(),
  unitSeries: z.string().nullable(),
  // WarehouseIdentityReason, read leniently. Pairs with unitSeries above.
  unitSeriesUnavailableReason: z.string().nullable(),
  techSize: z.string().nullable(),
  weightGrams: money,
  weightUnavailableReason: z.string().nullable(),
  // The state 1C recorded on the piece, not its published status. Exactly one
  // of the pair is set.
  lifecycle: UnitLifecycleSchema.nullable(),
  // WarehouseFactReasonSchema, read leniently.
  lifecycleUnavailableReason: z.string().nullable().optional().default(null),
  problems: z.array(InventoryProblemSchema).optional().default([]),
  problemCount: z.number().int().nonnegative().optional().default(0),
  problemsAvailable: z.boolean().optional().default(false),
  problemsUnavailableReason: z.string().nullable().optional().default(null),
}).strict().superRefine((unit, context) => {
  if (unit.problemCount !== unit.problems.length) {
    context.addIssue({ code: 'custom', path: ['problemCount'], message: 'problemCount must equal problems.length' })
  }
})

export const InventoryGroupChildrenSchema = z.object({
  variants: z.array(z.object({
    productId: z.string().uuid(),
    title: z.string().nullable(),
    techSize: z.string().nullable(),
    primaryMediaUrl: z.string().nullable(),
    problems: z.array(InventoryProblemSchema).optional().default([]),
    problemCount: z.number().int().nonnegative().optional().default(0),
    problemsAvailable: z.boolean().optional().default(false),
    problemsUnavailableReason: z.string().nullable().optional().default(null),
  }).strict().superRefine((variant, context) => {
    if (variant.problemCount !== variant.problems.length) {
      context.addIssue({ code: 'custom', path: ['problemCount'], message: 'problemCount must equal problems.length' })
    }
  })).optional().default([]),
  variantTotal: z.number().int().nonnegative().optional().default(0),
  parentKey: z.string().min(1),
  parentLevel: InventoryGroupLevelSchema,
  childLevel: InventoryGroupChildLevelSchema,
  groups: z.array(InventoryGroupRowSchema),
  units: z.array(InventoryGroupUnitSchema),
  rows: z.array(InventoryRowSchema).optional().default([]),
  unlinkedLines: z.array(z.object({
    position: z.number().int(),
    name: z.string(),
    quantity: z.number().int().nonnegative(),
    amount: money,
  }).strict()).optional().default([]),
  // The child POPULATION, not the length of what came back. `Показать все N`
  // reads this.
  total: z.number().int().nonnegative(),
  limit: z.number().int().positive(),
  offset: z.number().int().nonnegative(),
  lifecycle: WarehouseStatusScopeSchema,
  descriptionPresence: DescriptionPresenceSchema.nullish(),
  contractVersion: z.string().nullish(),
}).strict()

export type InventoryGroupLevel = z.infer<typeof InventoryGroupLevelSchema>
export type InventoryGroupChildLevel = z.infer<typeof InventoryGroupChildLevelSchema>
export type InventoryGroupReason = z.infer<typeof InventoryGroupReasonSchema>
export type GroupWeight = z.infer<typeof GroupWeightSchema>
export type GroupStockConflict = z.infer<typeof GroupStockConflictSchema>
export type InventoryGroupRow = z.infer<typeof InventoryGroupRowSchema>
export type ModelLevelAvailability = z.infer<typeof ModelLevelAvailabilitySchema>
export type InventoryGroupPage = z.infer<typeof InventoryGroupPageSchema>
export type InventoryGroupUnit = z.infer<typeof InventoryGroupUnitSchema>
export type InventoryGroupChildren = z.infer<typeof InventoryGroupChildrenSchema>
export type InventoryProblem = z.infer<typeof InventoryProblemSchema>
export type WarehouseSource = z.infer<typeof WarehouseSourceSchema>
export type WarehouseProductTypeCode = z.infer<typeof WarehouseProductTypeCodeSchema>
export type DescriptionPresence = z.infer<typeof DescriptionPresenceSchema>

export type ProposalState = z.infer<typeof ProposalStateSchema>
export type IdentityOrigin = z.infer<typeof IdentityOriginSchema>
export type OnecCodeParseState = z.infer<typeof OnecCodeParseStateSchema>
export type LocalStockState = z.infer<typeof LocalStockStateSchema>
export type InventoryIncludeFacet = z.infer<typeof InventoryIncludeFacetSchema>
export type MissingTaxonomyFacet = z.infer<typeof MissingTaxonomyFacetSchema>
export type TaxonomyFact = z.infer<typeof TaxonomyFactSchema>
export type MarketplaceBinding = z.infer<typeof MarketplaceBindingSchema>
export type Provenance = z.infer<typeof ProvenanceSchema>
export type VariantIdentity = z.infer<typeof VariantIdentitySchema>
export type ProductAttributes = z.infer<typeof ProductAttributesSchema>
export type MarketplacePresence = z.infer<typeof MarketplacePresenceSchema>
export type MarketplaceListing = z.infer<typeof MarketplaceListingSchema>
export type FieldObservation = z.infer<typeof FieldObservationSchema>
export type FieldComparison = z.infer<typeof FieldComparisonSchema>
export type StockDisagreement = z.infer<typeof StockDisagreementSchema>
export type StockMismatchReason = z.infer<typeof StockMismatchReasonSchema>
export type StockMismatchFinding = z.infer<typeof StockMismatchFindingSchema>
export type StockMismatchPiece = z.infer<typeof StockMismatchPieceSchema>
export type StockMismatchSignal = z.infer<typeof StockMismatchSignalSchema>
export type ReconciliationProposal = z.infer<typeof ReconciliationProposalSchema>
export type AvailableFilters = z.infer<typeof AvailableFiltersSchema>
export type ProviderSlot = z.infer<typeof ProviderSlotSchema>
export type VariantReconciliation = z.infer<typeof VariantReconciliationSchema>
export type UnitReaderReason = z.infer<typeof UnitReaderReasonSchema>
export type IdentifiedUnit = z.infer<typeof IdentifiedUnitSchema>
export type InventoryVariantUnavailable = z.infer<typeof InventoryVariantUnavailableSchema>
export type InventoryVariantUnavailableReason = z.infer<typeof InventoryVariantUnavailableReasonSchema>
export type UnitPolicyGate = z.infer<typeof UnitPolicyGateSchema>
export type UnitEligibilityBlocker = z.infer<typeof UnitEligibilityBlockerSchema>
export type UnitAllocationState = z.infer<typeof UnitAllocationStateSchema>
export type UnitAllocationReason = z.infer<typeof UnitAllocationReasonSchema>
export type UnitAvailabilityState = z.infer<typeof UnitAvailabilityStateSchema>
export type UnitAvailabilityBlocker = z.infer<typeof UnitAvailabilityBlockerSchema>
export type UnitAllocationCoverage = z.infer<typeof UnitAllocationCoverageSchema>
export type UnitAllocationProvenance = z.infer<typeof UnitAllocationProvenanceSchema>
export type AllocationRecord = z.infer<typeof AllocationRecordSchema>
export type AllocationReason = z.infer<typeof AllocationReasonSchema>
export type UnresolvedPopulation = z.infer<typeof UnresolvedPopulationSchema>
export type PopulationWindow = z.infer<typeof PopulationWindowSchema>
export type UnitWarehouse = z.infer<typeof UnitWarehouseSchema>
export type UnitLifecycle = z.infer<typeof UnitLifecycleSchema>
export type CostUnavailableReason = z.infer<typeof CostUnavailableReasonSchema>
export type UnitListingSort = z.infer<typeof UnitListingSortSchema>
export type UnitListingDirection = z.infer<typeof UnitListingDirectionSchema>
export type InventorySortDirection = z.infer<typeof InventorySortDirectionSchema>
export type InventoryPageSort = z.infer<typeof InventoryPageSortSchema>
export type InventoryOrderGroupSort = z.infer<typeof InventoryOrderGroupSortSchema>
export type UnitLifecycleScope = z.infer<typeof UnitLifecycleScopeSchema>
export type PublishedProductStatus = z.infer<typeof PublishedProductStatusSchema>
export type WarehouseStatusScope = z.infer<typeof WarehouseStatusScopeSchema>
export type UnitListingRow = z.infer<typeof UnitListingRowSchema>
export type UnitListingPage = z.infer<typeof UnitListingPageSchema>
export type ProductMediaKind = z.infer<typeof ProductMediaKindSchema>
export type ProductMediaStatus = z.infer<typeof ProductMediaStatusSchema>
export type ProductMediaUnavailableReason = z.infer<typeof ProductMediaUnavailableReasonSchema>
export type InventoryProductNotFoundReason = z.infer<typeof InventoryProductNotFoundReasonSchema>
export type InventoryProductNotFound = z.infer<typeof InventoryProductNotFoundSchema>
export type ProductMediaProbeUnavailableReason = z.infer<typeof ProductMediaProbeUnavailableReasonSchema>
export type ProductMediaItem = z.infer<typeof ProductMediaItemSchema>
export type ProductMedia = z.infer<typeof ProductMediaSchema>
export type ProductMediaWrite = z.infer<typeof ProductMediaWriteSchema>
export type ProductMediaOrder = z.infer<typeof ProductMediaOrderSchema>

/**
 * The variation feed: the other articles of one product model.
 *
 * The differentiator is a SERVER string. Nothing in this schema carries a
 * member's material, fineness, shape or product type, so there is nothing here
 * for a client to compute a differentiator from — the refusal is the absence of
 * the inputs rather than a rule somebody has to remember.
 */
export const ModelDifferentiatorFieldSchema = z.enum([
  'material',
  'fineness',
  'shape',
  'product_type',
])
export const ModelDifferentiatorUnavailableReasonSchema = z.enum([
  'differentiator.single_member',
  'differentiator.members_do_not_differ',
  'differentiator.member_evidence_absent',
  'differentiator.member_field_value_absent',
  'differentiator.evidence_source_unavailable',
])
// A state that selects its exact translated rendering, so it is strict. The
// browser does not collapse these members into a second business verdict.
export const PublicationStateSchema = z.enum([
  'submitted',
  'live',
  'drifted',
  'paused',
  'awaiting_review',
  'rejected',
  'archived',
  'unknown',
])
export const PublicationGroupReasonSchema = z.enum([
  'publication.state_not_evidenced',
  'publication_group.reason_not_published',
])

// One provider account's state, never one collapsed verdict. An empty list of
// these means no publication group row exists; it is not a claim that the model
// is unconfirmed, and rendering it as one would tell the operator something
// untrue.
export const PublicationGroupSchema = z.object({
  providerCode: z.string(),
  accountCode: z.string(),
  displayName: z.string(),
  state: PublicationStateSchema,
  // PublicationGroupReasonSchema, read leniently. See DeploySkewed above.
  unavailableReason: z.string().nullable(),
  remoteGroupCode: z.string().nullable(),
}).strict().refine(
  (entry) => (entry.state === 'unknown') === (entry.unavailableReason !== null),
  { message: 'an unknown publication state must name its reason, and only then' },
)

export const ModelGroupMemberSchema = z.object({
  productId: z.string().uuid(),
  article: z.string().nullable(),
  // WarehouseIdentityReasonSchema on both, read leniently.
  articleUnavailableReason: z.string().nullable(),

  isCurrent: z.boolean(),
  thumbnailUrl: z.string().nullable(),
  // ProductMediaUnavailableReasonSchema, read leniently; the sentence is the
  // server's and is rendered verbatim.
  thumbnailUnavailableReason: z.string().nullable(),
  thumbnailUnavailableMessage: z.string().min(1).max(240).nullable(),
  differentiator: z.string().min(1).nullable(),
  // ModelDifferentiatorUnavailableReasonSchema, read leniently.
  differentiatorUnavailableReason: z.string().nullable(),
  differentiatorUnavailableMessage: z.string().min(1).max(240).nullable(),
}).strict().refine(
  (member) => (member.article === null) !== (member.articleUnavailableReason === null),
  { message: 'article and its unavailable reason are exclusive' },
).refine(
  (member) => (member.thumbnailUrl === null) !== (member.thumbnailUnavailableReason === null),
  { message: 'thumbnailUrl and thumbnailUnavailableReason are exclusive' },
).refine(
  (member) => (member.thumbnailUnavailableReason === null) === (member.thumbnailUnavailableMessage === null),
  { message: 'a thumbnail reason and its message travel together' },
).refine(
  (member) => (member.differentiator === null) !== (member.differentiatorUnavailableReason === null),
  { message: 'differentiator and its unavailable reason are exclusive' },
).refine(
  (member) => (member.differentiatorUnavailableReason === null) === (member.differentiatorUnavailableMessage === null),
  { message: 'a differentiator reason and its message travel together' },
)

export const ModelGroupDetailSchema = z.object({
  modelId: z.string().uuid(),
  modelArticle: z.string(),
  // The population, never the length of `members`. A truncated page is visible
  // as memberCount > members.length rather than as a smaller group.
  // Zero is reachable and is not the unknown-model case: the model is named,
  // and the head naming it while the membership ledger says the product left is
  // a contradiction between two writers rather than a missing model.
  memberCount: z.number().int().nonnegative(),
  differentiatingFields: z.array(ModelDifferentiatorFieldSchema),
  publicationGroups: z.array(PublicationGroupSchema),
  members: z.array(ModelGroupMemberSchema),
}).strict().refine(
  (group) => group.memberCount >= group.members.length,
  { message: 'memberCount is a population count and cannot under-report' },
)

/**
 * Three absences, three values. `group` and `modelUnavailableReason` are
 * exclusive, so a model with exactly one member (a group whose `memberCount` is
 * 1) can never be read as an unknown model (`group: null`, `isUngrouped` true).
 * The schema refuses a body that collapses them.
 */
export const ModelGroupSchema = z.object({
  contractVersion: z.string(),
  productId: z.string().uuid(),
  isUngrouped: z.boolean(),
  // ModelGroupingReasonSchema, read leniently.
  modelUnavailableReason: z.string().nullable(),
  group: ModelGroupDetailSchema.nullable(),
}).strict().refine(
  (body) => (body.group === null) !== (body.modelUnavailableReason === null),
  { message: 'group and modelUnavailableReason are exclusive' },
).refine(
  (body) => body.isUngrouped === (body.group === null),
  { message: 'isUngrouped and the absence of a group are one fact' },
)

export type ModelDifferentiatorField = z.infer<typeof ModelDifferentiatorFieldSchema>
export type ModelDifferentiatorUnavailableReason = z.infer<typeof ModelDifferentiatorUnavailableReasonSchema>
export type PublicationState = z.infer<typeof PublicationStateSchema>
export type PublicationGroupReason = z.infer<typeof PublicationGroupReasonSchema>
export type PublicationGroup = z.infer<typeof PublicationGroupSchema>
export type ModelGroupMember = z.infer<typeof ModelGroupMemberSchema>
export type ModelGroupDetail = z.infer<typeof ModelGroupDetailSchema>
export type ModelGroup = z.infer<typeof ModelGroupSchema>
